import { useState, useEffect, useRef } from "react";

/* ─── GLOBAL STYLES injected once ─── */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=DM+Mono:wght@300;400;500&family=DM+Sans:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }

  body {
    background: #0d0f14;
    color: #e8ecf4;
    font-family: 'DM Sans', sans-serif;
    font-size: 16px;
    line-height: 1.7;
    overflow-x: hidden;
  }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: #0d0f14; }
  ::-webkit-scrollbar-thumb { background: #252a3a; border-radius: 2px; }

  @keyframes bobble {
    0%,100% { transform: translateY(0); }
    50%      { transform: translateY(8px); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .bobble  { animation: bobble 2s ease-in-out infinite; }
  .fade-up { animation: fadeUp 0.6s ease both; }

  /* ── NAV ── */
  .nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
    background: rgba(13,15,20,0.92); backdrop-filter: blur(12px);
    border-bottom: 1px solid #252a3a;
    display: flex; align-items: center; height: 52px; padding: 0 24px;
  }
  .nav-logo {
    font-family: 'Playfair Display', serif; font-size: 15px; font-weight: 700;
    color: #4f8ef7; letter-spacing: .02em; margin-right: 32px; white-space: nowrap;
    cursor: pointer; user-select: none;
  }
  .nav-chapters {
    display: flex; gap: 4px; overflow-x: auto; scrollbar-width: none;
  }
  .nav-chapters::-webkit-scrollbar { display: none; }
  .nav-ch {
    font-family: 'DM Mono', monospace; font-size: 11px; color: #7a859e;
    padding: 5px 12px; border-radius: 4px; cursor: pointer; white-space: nowrap;
    transition: all .2s; border: none; background: none;
  }
  .nav-ch:hover  { color: #e8ecf4; background: #1c2030; }
  .nav-ch.active { color: #4f8ef7; background: rgba(79,142,247,.12); }

  /* ── PROGRESS ── */
  .progress-track { position: fixed; top: 52px; left:0; right:0; height:2px; background:#252a3a; z-index:999; }
  .progress-bar   { height:100%; background: linear-gradient(90deg,#4f8ef7,#4ff79e); transition: width .1s; }

  /* ── HERO ── */
  .hero {
    min-height: 100vh; display: flex; align-items: center; justify-content: center;
    text-align: center; padding: 80px 24px 60px; position: relative; overflow: hidden;
  }
  .hero-grid {
    position: absolute; inset: 0;
    background-image: linear-gradient(#252a3a 1px,transparent 1px), linear-gradient(90deg,#252a3a 1px,transparent 1px);
    background-size: 48px 48px; opacity: .35;
  }
  .hero-glow {
    position: absolute; width:600px; height:600px; border-radius:50%;
    background: radial-gradient(circle,rgba(79,142,247,.12) 0%,transparent 70%);
    top:50%; left:50%; transform:translate(-50%,-50%); pointer-events:none;
  }
  .hero-content { position:relative; z-index:1; max-width:720px; }
  .hero-tag {
    font-family:'DM Mono',monospace; font-size:11px; color:#4f8ef7;
    letter-spacing:.15em; text-transform:uppercase; margin-bottom:20px; opacity:.8;
  }
  .hero h1 {
    font-family:'Playfair Display',serif; font-size:clamp(38px,7vw,72px); font-weight:900;
    line-height:1.1; margin-bottom:24px;
    background: linear-gradient(135deg,#fff 0%,#a8c4f0 60%,#4f8ef7 100%);
    -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text;
  }
  .hero p { font-size:18px; color:#7a859e; max-width:540px; margin:0 auto 40px; line-height:1.75; }
  .hero-scroll {
    font-family:'DM Mono',monospace; font-size:12px; color:#3d4560;
    letter-spacing:.1em; text-transform:uppercase;
    display:flex; flex-direction:column; align-items:center; gap:8px; margin-top:60px;
  }
  .badge-row { display:flex; gap:10px; justify-content:center; flex-wrap:wrap; margin-top:16px; }
  .badge {
    font-family:'DM Mono',monospace; font-size:10px; padding:4px 10px;
    border-radius:20px; letter-spacing:.08em; text-transform:uppercase;
  }
  .badge-hld { background:rgba(79,142,247,.15); color:#4f8ef7; border:1px solid rgba(79,142,247,.25); }
  .badge-lld { background:rgba(247,130,79,.15); color:#f7824f; border:1px solid rgba(247,130,79,.25); }

  /* ── TOC ── */
  .toc-section { border-top:1px solid #252a3a; padding:60px 24px; background:#141720; }
  .toc-inner   { max-width:800px; margin:0 auto; }
  .toc-title   { font-family:'DM Mono',monospace; font-size:11px; color:#3d4560; letter-spacing:.15em; text-transform:uppercase; margin-bottom:24px; }
  .toc-grid    { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:12px; }
  .toc-item    { background:#141720; border:1px solid #252a3a; border-radius:8px; padding:16px 18px; cursor:pointer; transition:all .2s; text-decoration:none; display:block; }
  .toc-item:hover { border-color:#4f8ef7; background:#1c2030; transform:translateY(-2px); }
  .toc-item.lld { border-left:3px solid rgba(247,130,79,.4); }
  .toc-item.hld { border-left:3px solid rgba(79,142,247,.4); }
  .toc-num    { font-family:'DM Mono',monospace; font-size:10px; color:#3d4560; margin-bottom:6px; }
  .toc-item.lld .toc-num { color:#f7824f; }
  .toc-item.hld .toc-num { color:#4f8ef7; }
  .toc-label  { font-size:14px; font-weight:500; color:#e8ecf4; }
  .toc-sub    { font-size:12px; color:#7a859e; margin-top:3px; }

  /* ── SECTION DIVIDER ── */
  .section-divider { text-align:center; padding:48px 24px; border-top:1px solid #252a3a; }
  .section-divider h2 { font-family:'Playfair Display',serif; font-size:clamp(24px,4vw,40px); font-weight:700; margin-bottom:12px; color:#fff; }
  .section-divider p  { color:#3d4560; font-size:14px; font-family:'DM Mono',monospace; letter-spacing:.08em; text-transform:uppercase; }

  /* ── SCROLL SECTIONS (sticky left) ── */
  .scroll-section { display:grid; grid-template-columns:380px 1fr; min-height:100vh; border-top:1px solid #252a3a; }
  .scroll-left {
    position:sticky; top:52px; height:calc(100vh - 52px); overflow-y:auto;
    padding:64px 48px; border-right:1px solid #252a3a;
    scrollbar-width:thin; scrollbar-color:#252a3a transparent;
  }
  .scroll-right { padding:64px 48px; display:flex; flex-direction:column; gap:64px; }

  .chapter-num   { font-family:'DM Mono',monospace; font-size:10px; color:#4f8ef7; letter-spacing:.15em; text-transform:uppercase; margin-bottom:12px; }
  .chapter-title { font-family:'Playfair Display',serif; font-size:28px; font-weight:700; line-height:1.25; margin-bottom:20px; color:#fff; }
  .chapter-body  { font-size:15px; color:#7a859e; line-height:1.8; }
  .chapter-body p { margin-bottom:16px; }
  .chapter-body strong { color:#e8ecf4; font-weight:500; }
  .term  { color:#4ff79e; font-weight:500; font-style:italic; }

  .highlight-box {
    background:rgba(247,216,79,.12); border:1px solid rgba(247,216,79,.2);
    border-radius:4px; padding:12px 16px; margin:20px 0;
    font-size:14px; color:#f7d84f;
  }
  .note-box {
    background:rgba(79,142,247,.08); border-left:3px solid #4f8ef7;
    padding:12px 16px; margin:20px 0; font-size:13px; color:#7a859e;
    border-radius:0 4px 4px 0;
  }

  /* ── PANEL ── */
  .panel { background:#141720; border:1px solid #252a3a; border-radius:12px; padding:32px; position:relative; overflow:hidden; }
  .panel-label { font-family:'DM Mono',monospace; font-size:10px; color:#3d4560; letter-spacing:.12em; text-transform:uppercase; margin-bottom:20px; }

  /* ── METRICS ── */
  .metrics-row { display:flex; gap:12px; flex-wrap:wrap; margin:24px 0; }
  .metric-card { flex:1; min-width:100px; background:#1c2030; border:1px solid #252a3a; border-radius:8px; padding:14px 16px; text-align:center; }
  .metric-val  { font-family:'DM Mono',monospace; font-size:22px; font-weight:500; color:#4f8ef7; display:block; }
  .metric-val.orange { color:#f7824f; }
  .metric-val.green  { color:#4ff79e; }
  .metric-label { font-size:11px; color:#3d4560; margin-top:4px; }

  /* ── COMPARE TABLE ── */
  .compare-table { width:100%; border-collapse:collapse; font-size:13px; }
  .compare-table th { font-family:'DM Mono',monospace; font-size:10px; text-transform:uppercase; letter-spacing:.1em; color:#3d4560; padding:8px 12px; text-align:left; border-bottom:1px solid #252a3a; }
  .compare-table td { padding:10px 12px; border-bottom:1px solid rgba(37,42,58,.5); color:#7a859e; vertical-align:top; }
  .compare-table tr:hover td { background:#1c2030; }
  .compare-table td:first-child { color:#e8ecf4; font-weight:500; }
  .ctag { display:inline-block; font-family:'DM Mono',monospace; font-size:9px; padding:2px 7px; border-radius:3px; font-weight:500; letter-spacing:.05em; }
  .ctag-green  { background:rgba(79,247,158,.12); color:#4ff79e; }
  .ctag-orange { background:rgba(247,130,79,.12); color:#f7824f; }
  .ctag-blue   { background:rgba(79,142,247,.12); color:#4f8ef7; }
  .ctag-red    { background:rgba(247,79,142,.12); color:#f74f8e; }

  /* ── TIMELINE ── */
  .timeline { position:relative; padding-left:24px; border-left:2px solid #252a3a; margin:16px 0; }
  .tl-item  { position:relative; margin-bottom:24px; }
  .tl-dot   { width:10px; height:10px; background:#4f8ef7; border-radius:50%; position:absolute; left:-30px; top:4px; border:2px solid #0d0f14; }
  .tl-dot.orange { background:#f7824f; }
  .tl-dot.green  { background:#4ff79e; }
  .tl-title { font-size:14px; font-weight:500; color:#e8ecf4; margin-bottom:4px; }
  .tl-desc  { font-size:13px; color:#7a859e; }

  /* ── ACCORDION ── */
  .accordion-item   { border:1px solid #252a3a; border-radius:8px; margin-bottom:8px; overflow:hidden; }
  .accordion-header {
    width:100%; background:#141720; border:none; padding:14px 18px;
    display:flex; align-items:center; justify-content:space-between;
    cursor:pointer; font-size:14px; font-weight:500; color:#e8ecf4; text-align:left;
    transition:background .2s; font-family:'DM Sans',sans-serif;
  }
  .accordion-header:hover { background:#1c2030; }
  .acc-arrow { font-size:12px; color:#3d4560; transition:transform .2s; }
  .acc-arrow.open { transform:rotate(180deg); }
  .accordion-body { background:#0d0f14; font-size:13px; color:#7a859e; line-height:1.75; max-height:0; overflow:hidden; transition:max-height .35s ease, padding .2s; padding:0 18px; }
  .accordion-body.open { max-height:600px; padding:14px 18px; }

  /* ── STEPPER ── */
  .stepper { display:flex; gap:0; margin:20px 0; border:1px solid #252a3a; border-radius:8px; overflow:hidden; }
  .step { flex:1; text-align:center; padding:10px 8px; font-family:'DM Mono',monospace; font-size:11px; color:#3d4560; background:#141720; border-right:1px solid #252a3a; cursor:pointer; transition:all .2s; border-top:none; border-bottom:none; border-left:none; }
  .step:last-child { border-right:none; }
  .step.active { background:rgba(79,142,247,.12); color:#4f8ef7; }
  .step:hover:not(.active) { background:#1c2030; color:#7a859e; }
  .step-num { font-size:16px; display:block; margin-bottom:2px; }

  /* ── ICON GRID ── */
  .icon-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(130px,1fr)); gap:10px; margin:16px 0; }
  .icon-box  { background:#141720; border:1px solid #252a3a; border-radius:8px; padding:16px 12px; text-align:center; transition:all .2s; }
  .icon-box:hover { border-color:#4f8ef7; background:#1c2030; }
  .icon-box .icon  { font-size:24px; display:block; margin-bottom:8px; }
  .icon-box .label { font-size:12px; color:#7a859e; font-weight:500; }

  /* ── RECAP / FOOTER ── */
  .recap-section { padding:80px 24px; max-width:900px; margin:0 auto; border-top:1px solid #252a3a; }
  footer { border-top:1px solid #252a3a; padding:60px 24px; text-align:center; }
  footer h3 { font-family:'Playfair Display',serif; font-size:20px; margin-bottom:12px; color:#fff; }
  footer p  { font-size:13px; color:#3d4560; max-width:500px; margin:0 auto; }

  /* ── RESPONSIVE ── */
  @media(max-width:900px){
    .scroll-section { grid-template-columns:1fr; display:block; }
    .scroll-left { position:relative; top:0; height:auto; width:100%; border-right:none; border-bottom:1px solid #252a3a; padding:40px 20px; }
    .scroll-right { padding:32px 20px; }
  }
`;

/* ─── helpers ─── */
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

function useFadeIn() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { ref.current.style.opacity = 1; ref.current.style.transform = "translateY(0)"; }
    }, { threshold: 0.1 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return ref;
}

const FadePanel = ({ children, style = {} }) => {
  const ref = useFadeIn();
  return (
    <div ref={ref} className="panel" style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity .6s ease, transform .6s ease", ...style }}>
      {children}
    </div>
  );
};

/* ─── SVG arrow defs (shared) ─── */
const Defs = () => (
  <defs>
    <marker id="arr"  viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
      <path d="M0 0 L10 5 L0 10z" fill="#3d4560" />
    </marker>
    <marker id="arr-b" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
      <path d="M0 0 L10 5 L0 10z" fill="#4f8ef7" />
    </marker>
    <marker id="arr-g" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
      <path d="M0 0 L10 5 L0 10z" fill="#4ff79e" />
    </marker>
    <marker id="arr-o" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
      <path d="M0 0 L10 5 L0 10z" fill="#f7824f" />
    </marker>
    <marker id="arr-r" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
      <path d="M0 0 L10 5 L0 10z" fill="#f74f8e" />
    </marker>
    <marker id="arr-y" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
      <path d="M0 0 L10 5 L0 10z" fill="#f7d84f" />
    </marker>
  </defs>
);

const NL = ({ x1,y1,x2,y2,color="arr",dash=false,label,lx,ly }) => (
  <>
    <line x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={color==="arr"?"#3d4560":color==="arr-b"?"#4f8ef7":color==="arr-g"?"#4ff79e":color==="arr-o"?"#f7824f":color==="arr-r"?"#f74f8e":"#f7d84f"}
      strokeWidth={1.5} fill="none" strokeDasharray={dash?"4 3":undefined}
      markerEnd={`url(#${color})`} />
    {label && <text x={lx} y={ly} fontFamily="DM Mono,monospace" fontSize={9} fill="#3d4560" textAnchor="middle">{label}</text>}
  </>
);

const Box = ({ x,y,w,h,rx=8,variant="default",children }) => {
  const fills = {
    default:  { fill:"#1e2438",            stroke:"#252a3a" },
    primary:  { fill:"rgba(79,142,247,.08)", stroke:"#4f8ef7" },
    secondary:{ fill:"rgba(247,130,79,.08)", stroke:"#f7824f" },
    success:  { fill:"rgba(79,247,158,.08)", stroke:"#4ff79e" },
    danger:   { fill:"rgba(247,79,142,.08)", stroke:"#f74f8e" },
    yellow:   { fill:"rgba(247,216,79,.08)", stroke:"#f7d84f" },
    muted:    { fill:"#1c2030",             stroke:"#3d4560" },
  };
  const s = fills[variant] || fills.default;
  return <rect x={x} y={y} width={w} height={h} rx={rx} fill={s.fill} stroke={s.stroke} strokeWidth={1.5} />;
};

const TL = ({ x,y,text,size=11,color="#e8ecf4",anchor="middle",bold=false }) => (
  <text x={x} y={y} fontFamily="DM Mono,monospace" fontSize={size} fill={color} textAnchor={anchor} fontWeight={bold?"600":"400"}>{text}</text>
);

/* ─── ACCORDION ─── */
const Accordion = ({ items }) => {
  const [open, setOpen] = useState(null);
  return (
    <div>
      {items.map(({ key, header, body }) => (
        <div className="accordion-item" key={key}>
          <button className="accordion-header" onClick={() => setOpen(open === key ? null : key)}>
            <span dangerouslySetInnerHTML={{ __html: header }} />
            <span className={`acc-arrow ${open === key ? "open" : ""}`}>▼</span>
          </button>
          <div className={`accordion-body ${open === key ? "open" : ""}`}>{body}</div>
        </div>
      ))}
    </div>
  );
};

/* ─── HERO ─── */
const Hero = () => (
  <section className="hero" id="intro">
    <div className="hero-grid" />
    <div className="hero-glow" />
    <div className="hero-content">
      <div className="hero-tag">A Visual Guide · System Design</div>
      <h1>How Systems Are Designed</h1>
      <p>From a single class to a distributed system serving millions. A scroll-through visual exploration of Low Level and High Level Design principles every engineer should know.</p>
      <div className="badge-row">
        <span className="badge badge-lld">LLD — Low Level Design</span>
        <span className="badge badge-hld">HLD — High Level Design</span>
      </div>
      <div className="hero-scroll bobble">
        <span>scroll to explore</span>
        <span style={{ fontSize: 20 }}>↓</span>
      </div>
    </div>
  </section>
);

/* ─── CH 01: FUNDAMENTALS ─── */
const Ch01 = () => {
  const solidItems = [
    { key:"S", header:'<strong style="color:#4f8ef7">S</strong> — Single Responsibility', body:"A class should have only one reason to change. A UserService handles user logic — it shouldn't also send emails or connect to a database directly." },
    { key:"O", header:'<strong style="color:#4f8ef7">O</strong> — Open/Closed',           body:"Classes should be open for extension, closed for modification. Use interfaces and abstract classes so you can add new behaviour without touching existing code." },
    { key:"L", header:'<strong style="color:#4f8ef7">L</strong> — Liskov Substitution',   body:'Objects of a subclass must be replaceable for objects of the superclass without breaking the program. If Bird has fly(), then Penguin extends Bird is problematic.' },
    { key:"I", header:'<strong style="color:#4f8ef7">I</strong> — Interface Segregation', body:"No client should be forced to depend on methods it doesn't use. Prefer many small, specific interfaces over one large general one." },
    { key:"D", header:'<strong style="color:#4f8ef7">D</strong> — Dependency Inversion',  body:"High-level modules shouldn't depend on low-level modules. Both should depend on abstractions. Inject dependencies rather than hard-coding them." },
  ];

  return (
    <div className="scroll-section" id="fundamentals">
      <div className="scroll-left">
        <div className="chapter-num">Chapter 01 · LLD</div>
        <h2 className="chapter-title">What is Low Level Design?</h2>
        <div className="chapter-body">
          <p>When you write code that solves a problem, you're doing low level design. <strong>LLD</strong> is concerned with the detailed design of individual components — how classes are structured, how they talk to each other, and how complex behaviour emerges from simple rules.</p>
          <p>The four cornerstones of LLD come from <span className="term">Object-Oriented Programming</span>:</p>
          <div className="highlight-box">Good LLD means another engineer can read your code and understand it in minutes, not days.</div>
          <p>LLD feeds directly into <strong>SOLID principles</strong>, design patterns, and class diagrams — the vocabulary every engineer uses to describe component-level architecture.</p>
          <div className="note-box">In interviews, LLD questions often ask you to design a parking lot, a library system, or a chess game — start from entities, then relationships, then behaviours.</div>
        </div>
      </div>
      <div className="scroll-right">
        <FadePanel>
          <div className="panel-label">The Four OOP Pillars</div>
          <svg viewBox="0 0 560 280" width="100%" height="280">
            <Defs />
            <Box x={205} y={110} w={150} h={60} variant="primary" />
            <TL x={280} y={135} text="OOP" color="#4f8ef7" size={12} />
            <TL x={280} y={152} text="Object-Oriented" size={9} color="#3d4560" />

            <Box x={20}  y={30}  w={140} h={56} variant="secondary" />
            <TL x={90}   y={52}  text="Encapsulation" size={11} />
            <TL x={90}   y={68}  text="hide internal state" size={9} color="#3d4560" />

            <Box x={400} y={30}  w={140} h={56} variant="success" />
            <TL x={470}  y={52}  text="Abstraction" size={11} />
            <TL x={470}  y={68}  text="expose only interface" size={9} color="#3d4560" />

            <Box x={20}  y={194} w={140} h={56} variant="danger" />
            <TL x={90}   y={216} text="Inheritance" size={11} color="#f74f8e" />
            <TL x={90}   y={232} text="reuse via hierarchy" size={9} color="#3d4560" />

            <Box x={400} y={194} w={140} h={56} variant="yellow" />
            <TL x={470}  y={216} text="Polymorphism" size={11} color="#f7d84f" />
            <TL x={470}  y={232} text="one interface, many forms" size={9} color="#3d4560" />

            <NL x1={160} y1={58}  x2={205} y2={120} color="arr-o" />
            <NL x1={400} y1={58}  x2={355} y2={120} color="arr-g" />
            <NL x1={160} y1={222} x2={205} y2={160} color="arr-r" />
            <NL x1={400} y1={222} x2={355} y2={160} color="arr-y" />
          </svg>
        </FadePanel>
        <FadePanel>
          <div className="panel-label">SOLID Principles</div>
          <Accordion items={solidItems} />
        </FadePanel>
      </div>
    </div>
  );
};

/* ─── CH 02: CLASS DESIGN ─── */
const Ch02 = () => (
  <div className="scroll-section" id="lld">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 02 · LLD</div>
      <h2 className="chapter-title">Class Design & Relationships</h2>
      <div className="chapter-body">
        <p>Every system can be described as a set of <span className="term">entities</span> and the relationships between them. In LLD, these become classes and their connections.</p>
        <p>There are four types of relationships to master:</p>
        <ul style={{ margin:"12px 0 16px 16px", lineHeight:2, fontSize:14 }}>
          <li><strong>Association</strong> — uses a reference</li>
          <li><strong>Aggregation</strong> — "has-a" (can exist independently)</li>
          <li><strong>Composition</strong> — "part-of" (lifecycle dependent)</li>
          <li><strong>Inheritance</strong> — "is-a" hierarchy</li>
        </ul>
        <div className="highlight-box">Example: Design a Library System. Entities: Book, Member, Loan, Librarian. Relationships define the system.</div>
        <p>A <strong>UML Class Diagram</strong> is the standard visual language for expressing these relationships before writing any code.</p>
        <div className="note-box">Rule of thumb: prefer composition over inheritance. Inheritance creates tight coupling; composition gives you flexibility.</div>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">UML — Library System Class Diagram</div>
        <svg viewBox="0 0 560 340" width="100%" height="340">
          <Defs />
          {/* Library */}
          <Box x={200} y={10}  w={160} h={70} variant="primary" />
          <line x1={200} y1={30} x2={360} y2={30} stroke="#4f8ef7" strokeWidth={1} />
          <TL x={280} y={24}  text="Library" color="#4f8ef7" size={11} />
          <TL x={280} y={48}  text="name: String" size={9} color="#3d4560" />
          <TL x={280} y={62}  text="addBook() / searchBook()" size={9} color="#3d4560" />
          {/* Book */}
          <Box x={20}  y={160} w={150} h={80} variant="secondary" />
          <line x1={20} y1={180} x2={170} y2={180} stroke="#f7824f" strokeWidth={1} />
          <TL x={95}  y={174} text="Book" color="#f7824f" size={11} />
          <TL x={95}  y={196} text="isbn, title, author" size={9} color="#3d4560" />
          <TL x={95}  y={210} text="isAvailable: bool" size={9} color="#3d4560" />
          <TL x={95}  y={224} text="checkout() / return()" size={9} color="#3d4560" />
          {/* Member */}
          <Box x={390} y={160} w={150} h={80} variant="success" />
          <line x1={390} y1={180} x2={540} y2={180} stroke="#4ff79e" strokeWidth={1} />
          <TL x={465} y={174} text="Member" color="#4ff79e" size={11} />
          <TL x={465} y={196} text="memberId, name" size={9} color="#3d4560" />
          <TL x={465} y={210} text="borrowedBooks[]" size={9} color="#3d4560" />
          <TL x={465} y={224} text="borrow() / return()" size={9} color="#3d4560" />
          {/* Loan */}
          <Box x={200} y={270} w={160} h={60} variant="default" />
          <line x1={200} y1={288} x2={360} y2={288} stroke="#252a3a" strokeWidth={1} />
          <TL x={280} y={283} text="Loan" color="#f7d84f" size={11} />
          <TL x={280} y={303} text="dueDate, returnDate" size={9} color="#3d4560" />
          <TL x={280} y={317} text="calculateFine()" size={9} color="#3d4560" />
          {/* Lines */}
          <NL x1={200} y1={55}  x2={95}  y2={160} color="arr-o" dash label="contains ◆" lx={130} ly={108} />
          <NL x1={360} y1={55}  x2={465} y2={160} color="arr-g" dash label="registers ◇" lx={420} ly={108} />
          <NL x1={170} y1={220} x2={220} y2={270} color="arr" />
          <NL x1={390} y1={220} x2={340} y2={270} color="arr" />
          <TL x={280} y={258} text="1 Loan → 1 Book + 1 Member" size={9} color="#3d4560" />
        </svg>
      </FadePanel>
      <FadePanel>
        <div className="panel-label">Composition vs Inheritance</div>
        <pre style={{ background:"#080c14", borderRadius:8, padding:"16px 18px", fontFamily:"DM Mono,monospace", fontSize:12, lineHeight:1.75, color:"#c5d1e8", overflowX:"auto", margin:"0" }}>
{`// ❌ Inheritance: tight coupling
class FlyingBird extends Bird { fly() {...} }
class Penguin extends FlyingBird {
  fly() { throw "can't fly"; }   // 💥
}

// ✅ Composition: flexible
interface Flyable { fly(): void; }

class Eagle implements Flyable {
  constructor(private wings: WingBehavior) {}
  fly() { this.wings.flap(); }
}
// Penguin simply doesn't implement Flyable`}
        </pre>
      </FadePanel>
    </div>
  </div>
);

/* ─── CH 03: DESIGN PATTERNS ─── */
const PAT_CONTENT = {
  c: <><strong style={{ color:"#e8ecf4" }}>Creational patterns</strong> control object creation. They abstract the instantiation process, making a system independent of how its objects are created: <em>Singleton</em> (one instance), <em>Factory Method</em> (let subclasses decide), <em>Abstract Factory</em> (families of objects), <em>Builder</em> (step-by-step construction), <em>Prototype</em> (clone existing objects).</>,
  s: <><strong style={{ color:"#e8ecf4" }}>Structural patterns</strong> deal with object composition — how classes and objects combine into larger structures: <em>Adapter</em> (wrap incompatible interfaces), <em>Bridge</em> (decouple abstraction from implementation), <em>Composite</em> (tree structures), <em>Decorator</em> (add behaviour dynamically), <em>Facade</em> (simplified interface), <em>Proxy</em> (controlled access).</>,
  b: <><strong style={{ color:"#e8ecf4" }}>Behavioral patterns</strong> are about algorithms and assignment of responsibilities: <em>Observer</em> (event notification), <em>Strategy</em> (swap algorithms), <em>Command</em> (encapsulate actions), <em>Iterator</em> (traverse collections), <em>State</em> (change behaviour with state), <em>Template Method</em> (skeleton algorithm), <em>Chain of Responsibility</em>.</>,
};

const Ch03 = () => {
  const [pat, setPat] = useState("c");
  return (
    <div className="scroll-section" id="oop">
      <div className="scroll-left">
        <div className="chapter-num">Chapter 03 · LLD</div>
        <h2 className="chapter-title">Design Patterns</h2>
        <div className="chapter-body">
          <p><span className="term">Design patterns</span> are proven, reusable solutions to commonly occurring design problems. They're not code — they're blueprints.</p>
          <p>There are 23 classic GoF (Gang of Four) patterns grouped into three families:</p>
          <div className="stepper">
            {[{ k:"c", icon:"🏗️", label:"Creational" }, { k:"s", icon:"🔗", label:"Structural" }, { k:"b", icon:"🧩", label:"Behavioral" }].map(({ k, icon, label }) => (
              <button key={k} className={`step ${pat === k ? "active" : ""}`} onClick={() => setPat(k)}>
                <span className="step-num">{icon}</span>{label}
              </button>
            ))}
          </div>
          <div style={{ fontSize:14, color:"#7a859e", marginTop:12, lineHeight:1.8 }}>{PAT_CONTENT[pat]}</div>
          <div className="highlight-box">Most important for interviews: Singleton, Factory Method, Observer, Strategy, Decorator.</div>
        </div>
      </div>
      <div className="scroll-right">
        {/* Singleton */}
        <FadePanel>
          <div className="panel-label">Singleton Pattern — one instance, global access</div>
          <pre style={{ background:"#080c14", borderRadius:8, padding:"16px 18px", fontFamily:"DM Mono,monospace", fontSize:12, lineHeight:1.75, color:"#c5d1e8", overflowX:"auto" }}>
{`class DatabasePool {
  private static instance: DatabasePool;

  private constructor() {}   // ① prevent direct new

  static getInstance(): DatabasePool {
    if (!DatabasePool.instance) {
      DatabasePool.instance = new DatabasePool();
    }
    return DatabasePool.instance;  // ② always same obj
  }
}

const db1 = DatabasePool.getInstance();
const db2 = DatabasePool.getInstance();
console.log(db1 === db2); // true ✓`}
          </pre>
        </FadePanel>
        {/* Observer */}
        <FadePanel>
          <div className="panel-label">Observer Pattern — event-driven updates</div>
          <svg viewBox="0 0 560 200" width="100%" height="200">
            <Defs />
            <Box x={180} y={20} w={200} h={56} variant="yellow" />
            <TL x={280} y={44} text="Subject (EventEmitter)" color="#f7d84f" size={11} />
            <TL x={280} y={62} text="subscribe() / notify()" size={9} color="#3d4560" />

            <Box x={20}  y={130} w={130} h={50} variant="secondary" />
            <TL x={85}  y={151} text="EmailService" size={11} />
            <TL x={85}  y={167} text="onEvent(e)" size={9} color="#3d4560" />

            <Box x={215} y={130} w={130} h={50} variant="success" />
            <TL x={280} y={151} text="Analytics" size={11} />
            <TL x={280} y={167} text="onEvent(e)" size={9} color="#3d4560" />

            <Box x={410} y={130} w={130} h={50} variant="primary" />
            <TL x={475} y={151} text="PushNotify" size={11} color="#4f8ef7" />
            <TL x={475} y={167} text="onEvent(e)" size={9} color="#3d4560" />

            <NL x1={230} y1={76} x2={110} y2={130} color="arr-o" />
            <NL x1={280} y1={76} x2={280} y2={130} color="arr-g" />
            <NL x1={330} y1={76} x2={450} y2={130} color="arr-b" />
            <TL x={280} y={113} text="notify all observers →" size={9} color="#3d4560" />
          </svg>
        </FadePanel>
        {/* Pattern table */}
        <FadePanel>
          <div className="panel-label">Key Patterns Quick Reference</div>
          <table className="compare-table">
            <thead><tr><th>Pattern</th><th>Type</th><th>When to Use</th></tr></thead>
            <tbody>
              {[
                ["Singleton",  "ctag-blue",   "Creational",  "DB connections, config managers, loggers"],
                ["Factory",    "ctag-blue",   "Creational",  "Creating objects without specifying exact class"],
                ["Builder",    "ctag-blue",   "Creational",  "Complex object construction step by step"],
                ["Adapter",    "ctag-orange", "Structural",  "Make incompatible interfaces work together"],
                ["Decorator",  "ctag-orange", "Structural",  "Add behaviour without subclassing"],
                ["Observer",   "ctag-green",  "Behavioral",  "Event systems, pub-sub, state changes"],
                ["Strategy",   "ctag-green",  "Behavioral",  "Swap algorithms/behaviours at runtime"],
                ["Command",    "ctag-green",  "Behavioral",  "Undo/redo, queuing, transactions"],
              ].map(([name, cls, type, when]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td><span className={`ctag ${cls}`}>{type}</span></td>
                  <td>{when}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </FadePanel>
      </div>
    </div>
  );
};

/* ─── SECTION DIVIDER ─── */
const SectionDivider = ({ part, title, sub }) => (
  <div className="section-divider">
    <p>{part}</p>
    <h2>{title}</h2>
    {sub && <p style={{ color:"#3d4560", marginTop:8 }}>{sub}</p>}
  </div>
);

/* ─── CH 04: ARCHITECTURE ─── */
const Ch04 = () => (
  <div className="scroll-section" id="hld">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 04 · HLD</div>
      <h2 className="chapter-title">System Architecture Styles</h2>
      <div className="chapter-body">
        <p>Before writing a single line of code in a large system, you need to decide its <span className="term">architectural style</span>. This determines how components are organized, how they communicate, and how the system scales.</p>
        <p>The two dominant styles are <strong>Monolithic</strong> and <strong>Microservices</strong> architectures — and neither is universally better.</p>
        <div className="highlight-box">Most successful companies start with a monolith and migrate to microservices once they hit scale. Amazon, Netflix, Uber all made this journey.</div>
        <p>Other architectural patterns include <strong>Event-Driven Architecture</strong>, <strong>Serverless</strong>, <strong>CQRS</strong> (Command Query Responsibility Segregation), and <strong>Hexagonal Architecture</strong>.</p>
        <div className="note-box">For interviews: start by clarifying scale requirements. A system for 100 users vs 100 million users demands completely different architectures.</div>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">Monolith vs Microservices</div>
        <svg viewBox="0 0 560 280" width="100%" height="280">
          <Defs />
          {/* Monolith */}
          <TL x={130} y={20} text="Monolith" color="#f7d84f" size={12} />
          <Box x={30}  y={30} w={200} h={200} variant="muted" rx={10} />
          {[["UI Layer","#f7824f",48],["Business Logic","#4f8ef7",94],["Data Access","#4ff79e",140],["Database","#e8ecf4",186]].map(([label,color,y]) => (
            <g key={label}>
              <Box x={50} y={y} w={160} h={36} variant={color==="#f7824f"?"secondary":color==="#4f8ef7"?"primary":color==="#4ff79e"?"success":"default"} rx={4} />
              <TL x={130} y={y+22} text={label} size={10} color={color} />
            </g>
          ))}
          <TL x={130} y={250} text="Single deployable unit" size={10} color="#f7d84f" />
          {/* vs */}
          <TL x={276} y={148} text="vs" size={12} color="#3d4560" />
          {/* Microservices */}
          <TL x={430} y={20} text="Microservices" color="#4f8ef7" size={12} />
          {[["User Svc","#f7824f",310,35],["Order Svc","#4ff79e",460,35],["Payment","#f74f8e",310,130],["Notif.","#f7d84f",460,130]].map(([label,color,x,y]) => (
            <g key={label}>
              <Box x={x} y={y} w={90} h={44} variant={color==="#f7824f"?"secondary":color==="#4ff79e"?"success":color==="#f74f8e"?"danger":"yellow"} rx={6} />
              <TL x={x+45} y={y+18} text={label} size={10} color={color} />
              <TL x={x+45} y={y+33} text="own DB" size={9} color="#3d4560" />
            </g>
          ))}
          <Box x={355} y={88} w={90} h={32} variant="yellow" rx={5} />
          <TL x={400} y={108} text="API Gateway" size={9} color="#f7d84f" />
          <TL x={430} y={250} text="Independent deployable services" size={10} color="#4f8ef7" />
        </svg>
      </FadePanel>
      <FadePanel>
        <div className="panel-label">Architecture Trade-offs</div>
        <table className="compare-table">
          <thead><tr><th>Factor</th><th>Monolith</th><th>Microservices</th></tr></thead>
          <tbody>
            {[
              ["Complexity",       <span className="ctag ctag-green">Low</span>,      <span className="ctag ctag-red">High</span>],
              ["Deployment",       <span className="ctag ctag-green">Simple</span>,   <span className="ctag ctag-orange">Complex</span>],
              ["Scalability",      <span className="ctag ctag-orange">Limited</span>, <span className="ctag ctag-green">Per-service</span>],
              ["Team size",        <span className="ctag ctag-blue">Small</span>,     <span className="ctag ctag-green">Large</span>],
              ["Fault isolation",  <span className="ctag ctag-red">Poor</span>,       <span className="ctag ctag-green">Good</span>],
              ["Data management",  <span className="ctag ctag-green">Simple</span>,   <span className="ctag ctag-red">Complex</span>],
              ["Best for",         "MVP, startups",                                    "Scale, large teams"],
            ].map(([f,m,ms]) => (
              <tr key={String(f)}><td>{f}</td><td>{m}</td><td>{ms}</td></tr>
            ))}
          </tbody>
        </table>
      </FadePanel>
    </div>
  </div>
);

/* ─── CH 05: SCALABILITY ─── */
const Ch05 = () => (
  <div className="scroll-section" id="scalability">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 05 · HLD</div>
      <h2 className="chapter-title">Scalability</h2>
      <div className="chapter-body">
        <p>A system <span className="term">scales</span> when it can handle more load — more users, more data, more requests — without degrading performance.</p>
        <p>There are two directions you can scale:</p>
        <ul style={{ margin:"12px 0 16px 16px", lineHeight:2.2, fontSize:14 }}>
          <li><strong>Vertical scaling</strong> — bigger machine (more CPU/RAM)</li>
          <li><strong>Horizontal scaling</strong> — more machines</li>
        </ul>
        <p>Horizontal scaling requires <span className="term">load balancers</span> to distribute traffic. Load balancers can use Round Robin, Least Connections, IP Hash, or Weighted strategies.</p>
        <div className="highlight-box">Stateless services scale horizontally easily. Stateful services (holding session data) need sticky sessions or external state stores like Redis.</div>
        <p>Beyond load balancing, scalability depends on <strong>CDNs</strong> (static assets), <strong>caching</strong>, <strong>database optimisation</strong>, and <strong>async processing</strong>.</p>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">Load Balancer Architecture</div>
        <svg viewBox="0 0 560 260" width="100%" height="260">
          <Defs />
          <Box x={20}  y={100} w={80}  h={44} variant="default" />
          <TL x={60} y={126} text="Clients" size={11} />

          <Box x={200} y={88} w={120} h={68} variant="yellow" rx={8} />
          <TL x={260} y={116} text="Load" color="#f7d84f" size={11} />
          <TL x={260} y={132} text="Balancer" color="#f7d84f" size={11} />
          <TL x={260} y={148} text="Round Robin / Least Conn" size={9} color="#3d4560" />

          {[["Server 1","#4ff79e",20],["Server 2","#4ff79e",84],["Server 3","#4ff79e",148]].map(([label,color,y]) => (
            <g key={label}>
              <Box x={400} y={y}  w={130} h={44} variant="primary" rx={6} />
              <TL x={465} y={y+16} text={label} size={9} color="#e8ecf4" />
              <TL x={465} y={y+30} text="● healthy" size={9} color="#4ff79e" />
              <NL x1={320} y1={122} x2={398} y2={y+22} color="arr-g" />
            </g>
          ))}
          <Box x={400} y={212} w={130} h={44} variant="danger" rx={6} />
          <TL x={465} y={228} text="Server 4" size={9} color="#e8ecf4" />
          <TL x={465} y={244} text="✕ down" size={9} color="#f74f8e" />
          <line x1={320} y1={122} x2={400} y2={234} stroke="#f74f8e" strokeWidth={1} strokeDasharray="4 3" />
          <TL x={375} y={210} text="skipped" size={9} color="#f74f8e" />

          <NL x1={100} y1={122} x2={198} y2={122} color="arr" />
        </svg>
      </FadePanel>
      <FadePanel>
        <div className="panel-label">Scaling Metrics</div>
        <div className="metrics-row">
          {[["99.99%","Target SLA",""],["<100ms","P95 Latency","orange"],["10x","Scale factor","green"],["52min","Downtime/yr (99.99%)",""]].map(([val,label,cls]) => (
            <div className="metric-card" key={label}>
              <span className={`metric-val${cls?" "+cls:""}`}>{val}</span>
              <div className="metric-label">{label}</div>
            </div>
          ))}
        </div>
        <pre style={{ background:"#080c14", borderRadius:8, padding:"16px 18px", fontFamily:"DM Mono,monospace", fontSize:12, lineHeight:1.75, color:"#c5d1e8", overflowX:"auto" }}>
{`// Capacity planning
servers = Math.ceil(target_rps / rps_per_server)

// Little's Law: N = λ × W
// N = avg items in system
// λ = arrival rate (req/s)
// W = avg time in system (s)`}
        </pre>
      </FadePanel>
    </div>
  </div>
);

/* ─── CH 06: DATABASES ─── */
const Ch06 = () => (
  <div className="scroll-section" id="databases">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 06 · HLD</div>
      <h2 className="chapter-title">Database Design</h2>
      <div className="chapter-body">
        <p>The database is the heart of most systems. The choice between <span className="term">SQL</span> and <span className="term">NoSQL</span>, and how you design your schema, will affect performance, scalability, and maintainability for years.</p>
        <p><strong>Relational databases</strong> (PostgreSQL, MySQL) guarantee ACID properties and excel with structured, relational data and complex queries.</p>
        <p><strong>NoSQL databases</strong> sacrifice strict consistency for horizontal scale and flexible schemas. They come in four flavours: Document, Key-Value, Column-Family, and Graph.</p>
        <div className="highlight-box">Sharding splits data across multiple DB instances by a shard key. Replication copies data for read scaling and fault tolerance. They solve different problems.</div>
        <div className="note-box">For read-heavy workloads: add read replicas. For write-heavy or huge datasets: shard. For both: distributed databases like Cassandra or CockroachDB.</div>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">SQL vs NoSQL Decision Guide</div>
        <table className="compare-table">
          <thead><tr><th>Property</th><th>SQL (RDBMS)</th><th>NoSQL</th></tr></thead>
          <tbody>
            {[
              ["Schema",           "Fixed / rigid",              "Flexible / dynamic"],
              ["ACID",             <span className="ctag ctag-green">Full</span>,     <span className="ctag ctag-orange">Eventual</span>],
              ["Horizontal scale", <span className="ctag ctag-orange">Hard</span>,    <span className="ctag ctag-green">Native</span>],
              ["Joins",            <span className="ctag ctag-green">Yes</span>,      <span className="ctag ctag-red">Limited</span>],
              ["Query language",   "SQL (standard)",             "Varies"],
              ["Use cases",        "Finance, ERP, CRM",          "Social, IoT, catalog"],
              ["Examples",         "Postgres, MySQL",            "MongoDB, Cassandra"],
            ].map(([p,s,n]) => <tr key={String(p)}><td>{p}</td><td>{s}</td><td>{n}</td></tr>)}
          </tbody>
        </table>
      </FadePanel>
      <FadePanel>
        <div className="panel-label">Database Sharding — horizontal partitioning</div>
        <svg viewBox="0 0 560 220" width="100%" height="220">
          <Defs />
          <Box x={205} y={10} w={150} h={44} variant="primary" />
          <TL x={280} y={36} text="Application" color="#4f8ef7" size={11} />

          <Box x={205} y={80} w={150} h={44} variant="yellow" rx={8} />
          <TL x={280} y={100} text="Shard Router" color="#f7d84f" size={11} />
          <TL x={280} y={115} text="hash(user_id) % N" size={9} color="#3d4560" />

          <NL x1={280} y1={54} x2={280} y2={78} color="arr-b" />

          {[["Shard A","secondary",0,"users 0–33%"],["Shard B","success",215,"users 33–66%"],["Shard C","primary",430,"users 66–100%"]].map(([label,variant,x,sub]) => (
            <g key={label}>
              <Box x={x+30} y={168} w={130} h={44} variant={variant} rx={6} />
              <TL x={x+95} y={187} text={label} size={11} />
              <TL x={x+95} y={202} text={sub} size={9} color="#3d4560" />
              <NL x1={255} y1={124} x2={x+95} y2={168} color="arr-b" />
            </g>
          ))}
        </svg>
      </FadePanel>
      <FadePanel>
        <div className="panel-label">Indexes — the single biggest performance lever</div>
        <div className="chapter-body" style={{ fontSize:13 }}>
          <p>Without an index, a query scans every row — O(n). With a B-Tree index, it's O(log n). For a table with 1 billion rows, that's the difference between scanning 1B rows and ~30 comparisons.</p>
          <div className="metrics-row" style={{ margin:"16px 0" }}>
            {[["O(n)","Full scan","orange"],["O(log n)","B-Tree index","green"],["O(1)","Hash index",""]].map(([val,label,cls]) => (
              <div className="metric-card" key={label}>
                <span className={`metric-val${cls?" "+cls:""}`}>{val}</span>
                <div className="metric-label">{label}</div>
              </div>
            ))}
          </div>
          <div className="note-box">Trade-off: every index speeds reads but slows writes. Index only the columns you query on.</div>
        </div>
      </FadePanel>
    </div>
  </div>
);

/* ─── CH 07: CACHING ─── */
const Ch07 = () => (
  <div className="scroll-section" id="caching">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 07 · HLD</div>
      <h2 className="chapter-title">Caching</h2>
      <div className="chapter-body">
        <p>A <span className="term">cache</span> stores frequently accessed data in fast memory so you don't recompute or re-fetch it. It's often the single most impactful optimisation in a read-heavy system.</p>
        <p>Cache strategies differ in how they handle reads and writes:</p>
        <div className="timeline">
          {[
            { dot:"", title:"Cache-Aside (Lazy Loading)",  desc:"App reads cache first. On miss, reads DB, writes to cache. Most common pattern." },
            { dot:"orange", title:"Write-Through",          desc:"Every write goes to cache AND DB simultaneously. Always consistent, but slower writes." },
            { dot:"green",  title:"Write-Back (Write-Behind)", desc:"Write to cache only; flush to DB asynchronously. Fast writes, risk of data loss on crash." },
          ].map(({ dot, title, desc }) => (
            <div className="tl-item" key={title}>
              <div className={`tl-dot${dot?" "+dot:""}`} />
              <div className="tl-title">{title}</div>
              <div className="tl-desc">{desc}</div>
            </div>
          ))}
        </div>
        <div className="highlight-box">Cache eviction policies: LRU (Least Recently Used) is the default choice. Also consider LFU (Least Frequently Used) for skewed access patterns.</div>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">Cache-Aside Flow</div>
        <svg viewBox="0 0 560 260" width="100%" height="260">
          <Defs />
          <Box x={30}  y={100} w={100} h={44} variant="primary" />
          <TL x={80} y={126} text="App" color="#4f8ef7" size={11} />

          <Box x={220} y={30} w={120} h={44} variant="yellow" rx={6} />
          <TL x={280} y={50} text="Redis Cache" color="#f7d84f" size={10} />
          <TL x={280} y={65} text="key → value" size={9} color="#3d4560" />

          <Box x={220} y={180} w={120} h={44} variant="secondary" />
          <TL x={280} y={200} text="Database" size={11} />
          <TL x={280} y={215} text="source of truth" size={9} color="#3d4560" />

          <NL x1={130} y1={110} x2={218} y2={52}  color="arr-b" label="1. GET key" lx={160} ly={75} />
          <NL x1={218} y1={66}  x2={130} y2={118} color="arr-g" label="HIT → return" lx={140} ly={100} />
          <NL x1={130} y1={122} x2={218} y2={192} color="arr-o" dash label="MISS →" lx={165} ly={162} />
          <NL x1={280} y1={74}  x2={280} y2={178} color="arr"   dash label="2. query DB" lx={300} ly={130} />
          <NL x1={250} y1={178} x2={250} y2={74}  color="arr-o" label="3. SET key" lx={202} ly={135} />

          <Box x={390} y={30}  w={150} h={56} rx={6} variant="success" />
          <TL x={465} y={50}  text="Cache HIT" color="#4ff79e" size={10} />
          <TL x={465} y={64}  text="~0.1ms response" size={9} color="#3d4560" />
          <TL x={465} y={78}  text="(vs 10-100ms DB)" size={9} color="#3d4560" />

          <Box x={390} y={170} w={150} h={44} rx={6} variant="danger" />
          <TL x={465} y={190} text="Cache MISS" color="#f74f8e" size={10} />
          <TL x={465} y={205} text="DB query + SET cache" size={9} color="#3d4560" />
        </svg>
      </FadePanel>
      <FadePanel>
        <div className="panel-label">Redis Data Structures</div>
        <div className="icon-grid">
          {[["📝","String"],["🗂️","Hash"],["📋","List"],["🔢","Sorted Set"],["🔵","Set"],["📡","Pub/Sub"]].map(([icon,label]) => (
            <div className="icon-box" key={label}>
              <span className="icon">{icon}</span>
              <span className="label">{label}</span>
            </div>
          ))}
        </div>
        <div className="note-box">Sorted Sets are perfect for leaderboards. Hashes for session storage. Lists for queues and feeds.</div>
      </FadePanel>
    </div>
  </div>
);

/* ─── CH 08: MESSAGING ─── */
const Ch08 = () => (
  <div className="scroll-section" id="messaging">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 08 · HLD</div>
      <h2 className="chapter-title">Messaging & Event Queues</h2>
      <div className="chapter-body">
        <p>Synchronous (request-response) communication becomes a bottleneck at scale. <span className="term">Asynchronous messaging</span> decouples producers from consumers, allowing each to scale independently.</p>
        <p>Two key patterns:</p>
        <ul style={{ margin:"12px 0 16px 16px", lineHeight:2.2, fontSize:14 }}>
          <li><strong>Message Queue</strong> — point-to-point, each message consumed once (RabbitMQ, SQS)</li>
          <li><strong>Event Streaming</strong> — log-based, consumers can replay events (Kafka)</li>
        </ul>
        <div className="highlight-box">Kafka persists messages to disk in ordered, immutable logs. Multiple consumer groups can each independently read the full stream — perfect for event sourcing.</div>
        <div className="note-box">Use messaging for: email/notification sending, order processing, audit logs, event sourcing, decoupling microservices, handling traffic spikes.</div>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">Kafka Architecture</div>
        <svg viewBox="0 0 560 240" width="100%" height="240">
          <Defs />
          <Box x={10} y={80} w={100} h={44} variant="secondary" />
          <TL x={60} y={104} text="Producers" size={11} />

          {/* Broker */}
          <rect x={160} y={20} width={240} height={200} rx={10} fill="rgba(247,216,79,.04)" stroke="rgba(247,216,79,.2)" strokeWidth={1} />
          <TL x={280} y={40} text="Kafka Broker" color="#f7d84f" size={12} />

          {["Partition 0 → [msg1][msg2][msg3]","Partition 1 → [msg4][msg5][msg6]","Partition 2 → [msg7][msg8][msg9]"].map((label,i) => (
            <g key={i}>
              <rect x={175} y={50+i*50} width={210} height={30} rx={4} fill="rgba(79,142,247,.1)" stroke="rgba(79,142,247,.3)" strokeWidth={1} />
              <TL x={280} y={69+i*50} text={label} size={9} color="#4f8ef7" />
            </g>
          ))}
          <TL x={280} y={195} text="Offset-based ordering · durable log" size={9} color="#3d4560" />

          {["Consumer A","Consumer B","Consumer C"].map((label,i) => (
            <g key={label}>
              <Box x={450} y={50+i*60} w={100} h={36} variant="success" rx={6} />
              <TL x={500} y={72+i*60} text={label} size={10} color="#4ff79e" />
            </g>
          ))}

          <NL x1={110} y1={100} x2={158} y2={100} color="arr-o" />
          {[0,1,2].map(i => <NL key={i} x1={400} y1={65+i*50} x2={448} y2={68+i*60} color="arr-g" />)}
        </svg>
      </FadePanel>
    </div>
  </div>
);

/* ─── CH 09: MICROSERVICES ─── */
const Ch09 = () => (
  <div className="scroll-section" id="microservices">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 09 · HLD</div>
      <h2 className="chapter-title">Microservices & API Gateway</h2>
      <div className="chapter-body">
        <p>In a microservices architecture, each service owns its domain and communicates over a network. The <span className="term">API Gateway</span> is the single entry point for all clients — handling auth, rate limiting, routing, and request aggregation.</p>
        <p>Services communicate either <strong>synchronously</strong> (REST, gRPC) or <strong>asynchronously</strong> (message queues). Choosing wisely impacts consistency and coupling.</p>
        <div className="highlight-box">The <strong>Service Mesh</strong> (Istio, Linkerd) handles service-to-service communication: load balancing, mTLS encryption, circuit breaking, and observability — all without changing application code.</div>
        <p>Key resilience patterns:</p>
        <ul style={{ margin:"12px 0 16px 16px", lineHeight:2, fontSize:14 }}>
          <li><strong>Circuit Breaker</strong> — stop cascading failures</li>
          <li><strong>Retry with backoff</strong> — handle transient errors</li>
          <li><strong>Bulkhead</strong> — isolate failure domains</li>
          <li><strong>Timeout</strong> — never wait indefinitely</li>
        </ul>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">Microservices with API Gateway</div>
        <svg viewBox="0 0 560 300" width="100%" height="300">
          <Defs />
          <Box x={10} y={120} w={80} h={36} variant="default" />
          <TL x={50} y={140} text="Web App" size={9} />
          <Box x={10} y={168} w={80} h={36} variant="default" />
          <TL x={50} y={188} text="Mobile App" size={9} />

          <Box x={140} y={110} w={120} h={80} variant="yellow" rx={8} />
          <TL x={200} y={140} text="API Gateway" color="#f7d84f" size={11} />
          <TL x={200} y={157} text="Auth · Rate Limit" size={9} color="#3d4560" />
          <TL x={200} y={172} text="Route · Aggregate" size={9} color="#3d4560" />

          <NL x1={90} y1={138} x2={138} y2={148} color="arr" />
          <NL x1={90} y1={186} x2={138} y2={168} color="arr" />

          {[["User Svc","primary","#4f8ef7","Postgres",20],["Order Svc","secondary","#f7824f","MongoDB",80],["Payment","success","#4ff79e","Postgres",140],["Notif.","yellow","#f7d84f","Redis",200]].map(([label,variant,color,db,y]) => (
            <g key={label}>
              <Box x={340} y={y}  w={110} h={44} variant={variant} rx={6} />
              <TL x={395} y={y+18} text={label} size={10} color={color} />
              <TL x={395} y={y+33} text={`:${3001+["User Svc","Order Svc","Payment","Notif."].indexOf(label)}`} size={9} color="#3d4560" />
              <Box x={470} y={y}  w={80}  h={44} variant="muted" rx={4} />
              <TL x={510} y={y+26} text={db} size={9} color="#3d4560" />
              <NL x1={260} y1={150} x2={338} y2={y+22} color="arr-b" dash />
              <NL x1={450} y1={y+22} x2={468} y2={y+22} color="arr" dash />
            </g>
          ))}

          {/* Service mesh border */}
          <rect x={330} y={10} width={230} height={252} rx={10} fill="none" stroke="rgba(79,247,158,.15)" strokeWidth={1.5} strokeDasharray="5 4" />
          <TL x={445} y={278} text="Service Mesh (mTLS · circuit breaker)" size={9} color="#4ff79e" />
        </svg>
      </FadePanel>
    </div>
  </div>
);

/* ─── CH 10: CAP ─── */
const Ch10 = () => (
  <div className="scroll-section" id="cap">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 10 · HLD</div>
      <h2 className="chapter-title">CAP Theorem</h2>
      <div className="chapter-body">
        <p>The <span className="term">CAP Theorem</span> states that a distributed system can only guarantee two of three properties simultaneously:</p>
        <ul style={{ margin:"12px 0 16px 16px", lineHeight:2.4, fontSize:14 }}>
          <li><strong>Consistency</strong> — every read gets the latest write</li>
          <li><strong>Availability</strong> — every request gets a response</li>
          <li><strong>Partition Tolerance</strong> — system works despite network failures</li>
        </ul>
        <div className="highlight-box">Since network partitions are unavoidable in distributed systems, you're always choosing between CP and AP. Never CA in practice.</div>
        <p>The more nuanced <strong>PACELC theorem</strong> extends this: even without partitions, there's a trade-off between <em>latency</em> and <em>consistency</em>.</p>
        <div className="note-box">
          CP systems: HBase, Zookeeper, MongoDB (strict mode)<br />
          AP systems: Cassandra, DynamoDB, CouchDB
        </div>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">CAP Triangle</div>
        <svg viewBox="0 0 560 300" width="100%" height="300">
          <Defs />
          <polygon points="280,30 60,270 500,270" fill="rgba(79,142,247,.04)" stroke="#252a3a" strokeWidth={1.5} />
          {[{ cx:280, cy:30,  label:"C", sub:"Consistency",  color:"#4f8ef7" },
            { cx:60,  cy:270, label:"A", sub:"Availability", color:"#f7824f" },
            { cx:500, cy:270, label:"P", sub:"Partition",    color:"#4ff79e" }].map(({ cx,cy,label,sub,color }) => (
            <g key={label}>
              <circle cx={cx} cy={cy} r={36} fill={`${color}18`} stroke={color} strokeWidth={1.5} />
              <text x={cx} y={cy-4} fontFamily="Playfair Display,serif" fontSize={20} fill={color} textAnchor="middle" fontWeight="bold">{label}</text>
              <TL x={cx} y={cy+14} text={sub} size={9} color={color} />
            </g>
          ))}
          {[{ x:130, y:130, label:"CA", sub:"Traditional DB", color:"#f7824f" },
            { x:330, y:130, label:"CP", sub:"HBase, Zookeeper", color:"#4f8ef7" },
            { x:220, y:220, label:"AP", sub:"Cassandra, Dynamo", color:"#4ff79e" }].map(({ x,y,label,sub,color }) => (
            <g key={label}>
              <rect x={x} y={y} width={100} height={38} rx={5} fill={`${color}18`} stroke={`${color}50`} strokeWidth={1} />
              <TL x={x+50} y={y+16} text={label} size={11} color={color} />
              <TL x={x+50} y={y+30} text={sub} size={9} color="#3d4560" />
            </g>
          ))}
        </svg>
      </FadePanel>
      <FadePanel>
        <div className="panel-label">Consistency Levels</div>
        <table className="compare-table">
          <thead><tr><th>Level</th><th>Guarantee</th><th>Trade-off</th></tr></thead>
          <tbody>
            {[
              ["Strong",        "Always latest data",    <span className="ctag ctag-red">High latency</span>],
              ["Linearizable",  "Real-time ordering",    <span className="ctag ctag-red">Expensive</span>],
              ["Sequential",    "Same order for all",    <span className="ctag ctag-orange">Medium cost</span>],
              ["Eventual",      "Converges over time",   <span className="ctag ctag-green">High availability</span>],
              ["Causal",        "Causally linked ops",   <span className="ctag ctag-blue">Good balance</span>],
            ].map(([l,g,t]) => <tr key={l}><td>{l}</td><td>{g}</td><td>{t}</td></tr>)}
          </tbody>
        </table>
      </FadePanel>
    </div>
  </div>
);

/* ─── CH 11: REAL WORLD ─── */
const Ch11 = () => (
  <div className="scroll-section" id="realworld">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 11 · HLD</div>
      <h2 className="chapter-title">Designing Real Systems</h2>
      <div className="chapter-body">
        <p>A system design interview always starts with a vague question. Your job is to transform it into a <span className="term">concrete architecture</span> by systematically asking the right questions.</p>
        <div className="timeline">
          {[
            { dot:"",       title:"1. Clarify Requirements",  desc:"Functional (what it does) and non-functional (scale, latency, availability). Ask: how many users? reads vs writes ratio?" },
            { dot:"orange", title:"2. Estimate Scale",        desc:"Back-of-envelope calculations: QPS, storage needs, bandwidth. Drives architectural decisions." },
            { dot:"green",  title:"3. High-Level Design",     desc:"Draw the boxes: clients, API layer, services, data stores, caches, queues." },
            { dot:"",       title:"4. Deep Dive",             desc:"Pick the hardest part and design it in detail. DB schema, API contracts, data flow." },
          ].map(({ dot, title, desc }) => (
            <div className="tl-item" key={title}>
              <div className={`tl-dot${dot?" "+dot:""}`} />
              <div className="tl-title">{title}</div>
              <div className="tl-desc">{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">Example: URL Shortener (like bit.ly)</div>
        <svg viewBox="0 0 560 240" width="100%" height="240">
          <Defs />
          <Box x={10}  y={90} w={70}  h={44} variant="default" />
          <TL x={45} y={116} text="Client" size={9} />

          <Box x={120} y={90} w={80}  h={44} variant="primary" />
          <TL x={160} y={108} text="API" color="#4f8ef7" size={9} />
          <TL x={160} y={122} text="Service" size={9} color="#3d4560" />

          <Box x={250} y={30}  w={90}  h={44} variant="yellow" />
          <TL x={295} y={48}  text="Redis" color="#f7d84f" size={9} />
          <TL x={295} y={62}  text="shortCode→url" size={9} color="#3d4560" />

          <Box x={250} y={148} w={90}  h={44} variant="secondary" />
          <TL x={295} y={166} text="DB" size={9} />
          <TL x={295} y={180} text="shortCode,url,ts" size={9} color="#3d4560" />

          <Box x={390} y={90}  w={100} h={44} variant="success" />
          <TL x={440} y={108} text="301/302" color="#4ff79e" size={9} />
          <TL x={440} y={122} text="Redirect" size={9} color="#3d4560" />

          <Box x={390} y={160} w={150} h={44} variant="muted" />
          <TL x={465} y={178} text="Base62 encode" size={9} color="#3d4560" />
          <TL x={465} y={192} text="MD5(url) → 6 chars" size={9} color="#3d4560" />

          <NL x1={80}  y1={112} x2={118} y2={112} color="arr" />
          <NL x1={200} y1={100} x2={248} y2={52}  color="arr-b" label="GET" lx={215} ly={74} />
          <NL x1={200} y1={122} x2={248} y2={160} color="arr-o" dash label="MISS" lx={206} ly={148} />
          <NL x1={295} y1={74}  x2={295} y2={148} color="arr"   dash label="cold" lx={302} ly={115} />
          <NL x1={340} y1={112} x2={388} y2={112} color="arr-g" />
        </svg>
        <div className="note-box" style={{ marginTop:8 }}>Scale: 100M URLs/day → ~1,160 writes/sec and ~11,600 reads/sec. Cache hit ratio target: 80%+.</div>
      </FadePanel>

      <FadePanel>
        <div className="panel-label">Example: Twitter-like Feed (Fan-out)</div>
        <div className="chapter-body" style={{ fontSize:13 }}>
          <p style={{ marginBottom:16 }}>Two approaches to building a social feed — each with distinct trade-offs:</p>
          <table className="compare-table">
            <thead><tr><th></th><th>Fan-out on Write</th><th>Fan-out on Read</th></tr></thead>
            <tbody>
              {[
                ["How",           "Push tweet to all followers' feed cache on post", "Pull and merge tweets on read"],
                ["Read latency",  <span className="ctag ctag-green">Fast (pre-built)</span>,              <span className="ctag ctag-red">Slow (compute)</span>],
                ["Write cost",    <span className="ctag ctag-red">High (celebrities)</span>,              <span className="ctag ctag-green">Low</span>],
                ["Used when",     "Most users (<1M followers)",                      "Celebrities (1M+ followers)"],
              ].map(([k,a,b]) => <tr key={String(k)}><td>{k}</td><td>{a}</td><td>{b}</td></tr>)}
            </tbody>
          </table>
          <div className="note-box" style={{ marginTop:12 }}>Twitter uses a <strong>hybrid</strong>: fan-out on write for regular users, fan-out on read for celebrities (Lady Gaga, Justin Bieber cases).</div>
        </div>
      </FadePanel>
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════
   NEW CHAPTERS — added to reach full coverage
   Ch12: Concurrency & Thread Safety
   Ch13: API Design (REST / gRPC / GraphQL)
   Ch14: DNS & CDN
   Ch15: Consistent Hashing & Rate Limiting
   Ch16: Replication & Distributed Transactions
   Ch17: Security — Auth, JWT, OAuth2
   Ch18: Observability — Logs, Metrics, Traces
   Ch19: Remaining GoF Patterns (15 patterns)
   Ch20: LLD Practice Problems
   Ch21: Real-World Systems — YouTube, WhatsApp, Uber, Notification, Search
   Ch22: Capacity Estimation Framework
══════════════════════════════════════════════════════ */

/* ─── CH 12: CONCURRENCY ─── */
const Ch12 = () => {
  const items = [
    { key:"race", header:"Race Condition", body:"Two threads read-modify-write the same variable without synchronisation. Final value depends on execution order — nondeterministic. Fix: mutex/lock around the critical section." },
    { key:"dead", header:"Deadlock",       body:"Thread A holds Lock 1 and waits for Lock 2. Thread B holds Lock 2 and waits for Lock 1. Neither can proceed. Prevention: always acquire locks in a consistent global order." },
    { key:"live", header:"Livelock",       body:"Threads keep changing state in response to each other but make no progress — like two people stepping aside for each other in a hallway forever." },
    { key:"starv", header:"Starvation",    body:"A low-priority thread never gets CPU time because high-priority threads always preempt it. Fix: fair scheduling, priority ageing." },
  ];
  return (
    <div className="scroll-section" id="concurrency">
      <div className="scroll-left">
        <div className="chapter-num">Chapter 12 · LLD</div>
        <h2 className="chapter-title">Concurrency & Thread Safety</h2>
        <div className="chapter-body">
          <p>Modern systems handle thousands of simultaneous requests. Without careful design, shared state leads to <span className="term">race conditions</span>, <span className="term">deadlocks</span>, and data corruption.</p>
          <p>The fundamental primitive is the <strong>mutex (mutual exclusion lock)</strong> — only one thread can hold it at a time. A <strong>semaphore</strong> generalises this to N concurrent holders.</p>
          <div className="highlight-box">The safest concurrency strategy: share nothing. Stateless services + immutable data + message passing eliminate most concurrency bugs by design.</div>
          <p>When shared state is unavoidable, use <strong>atomic operations</strong> for simple counters, <strong>read-write locks</strong> for read-heavy data, and <strong>optimistic locking</strong> (versioned rows) at the database level.</p>
          <div className="note-box">In Java: synchronized, ReentrantLock, volatile. In Node.js: single-threaded event loop — concurrency bugs appear in async/await patterns instead.</div>
        </div>
      </div>
      <div className="scroll-right">
        <FadePanel>
          <div className="panel-label">Concurrency Primitives</div>
          <svg viewBox="0 0 560 220" width="100%" height="220">
            <Defs />
            {/* Mutex */}
            <Box x={20} y={20} w={240} h={80} variant="primary" rx={8} />
            <TL x={140} y={42} text="Mutex (Binary Lock)" color="#4f8ef7" size={11} />
            <TL x={140} y={58} text="lock() → only 1 thread enters" size={9} color="#3d4560" />
            <TL x={140} y={72} text="unlock() → next waiting thread" size={9} color="#3d4560" />
            <TL x={140} y={86} text="state: LOCKED | UNLOCKED" size={9} color="#3d4560" />
            {/* Semaphore */}
            <Box x={300} y={20} w={240} h={80} variant="success" rx={8} />
            <TL x={420} y={42} text="Semaphore (Counter)" color="#4ff79e" size={11} />
            <TL x={420} y={58} text="acquire() → count--" size={9} color="#3d4560" />
            <TL x={420} y={72} text="release() → count++" size={9} color="#3d4560" />
            <TL x={420} y={86} text="blocks when count == 0" size={9} color="#3d4560" />
            {/* Thread pool */}
            <Box x={20} y={130} w={520} h={70} variant="yellow" rx={8} />
            <TL x={280} y={152} text="Thread Pool" color="#f7d84f" size={11} />
            <TL x={280} y={168} text="Fixed N worker threads reused from queue — avoids thread creation overhead" size={9} color="#3d4560" />
            <TL x={280} y={184} text="Task Queue → [t1][t2][t3]…  →  Worker[1..N]" size={9} color="#f7d84f" />
          </svg>
        </FadePanel>
        <FadePanel>
          <div className="panel-label">Concurrency Hazards</div>
          <Accordion items={items} />
        </FadePanel>
        <FadePanel>
          <div className="panel-label">Thread-Safe Singleton (Double-Checked Locking)</div>
          <pre style={{ background:"#080c14", borderRadius:8, padding:"16px 18px", fontFamily:"DM Mono,monospace", fontSize:12, lineHeight:1.75, color:"#c5d1e8", overflowX:"auto" }}>
{`class Singleton {
  private static volatile instance: Singleton; // volatile!

  static getInstance(): Singleton {
    if (!this.instance) {              // 1st check (no lock)
      synchronized(Singleton.class) {
        if (!this.instance) {          // 2nd check (with lock)
          this.instance = new Singleton();
        }
      }
    }
    return this.instance;
  }
}
// volatile ensures visibility across CPU caches
// Double-check avoids lock overhead on every call`}
          </pre>
        </FadePanel>
      </div>
    </div>
  );
};

/* ─── CH 13: API DESIGN ─── */
const Ch13 = () => {
  const [tab, setTab] = useState("rest");
  const code = {
    rest: `// REST — Resource-based, HTTP verbs
GET    /users/42          → fetch user
POST   /users             → create user
PUT    /users/42          → full update
PATCH  /users/42          → partial update
DELETE /users/42          → delete user

// Versioning
GET /api/v1/users/42      // URI versioning (common)
GET /api/users/42         // Header: Accept: application/vnd.api.v2+json

// HTTP status codes that matter
200 OK | 201 Created | 204 No Content
400 Bad Request | 401 Unauthorized | 403 Forbidden
404 Not Found | 409 Conflict | 429 Too Many Requests
500 Internal Server Error | 503 Service Unavailable`,

    grpc: `// gRPC — Contract-first, binary Protobuf, HTTP/2
// Define in .proto file:
service UserService {
  rpc GetUser (GetUserRequest) returns (User);
  rpc CreateUser (CreateUserRequest) returns (User);
  rpc StreamUsers (Empty) returns (stream User); // server streaming
}

message User {
  int64 id = 1;
  string name = 2;
  string email = 3;
}

// Advantages over REST:
// ✓ Strongly typed (compile-time contract)
// ✓ ~7x smaller payload (binary vs JSON)
// ✓ Native streaming (bidirectional)
// ✓ Auto-generated clients in 10+ languages
// Use for: inter-service comms, mobile, streaming`,

    graphql: `// GraphQL — Query exactly what you need
// Client controls shape of response
query {
  user(id: 42) {
    name
    email
    orders(last: 5) {
      id
      total
      items { name, qty }
    }
  }
}
// No over-fetching (REST returns full object)
// No under-fetching (no N+1 requests needed)
// Single endpoint: POST /graphql
// Mutations for writes:
mutation {
  createOrder(userId: 42, items: [...]) { id }
}
// Subscriptions for real-time:
subscription { orderStatus(id: 99) { status } }`,
  };
  return (
    <div className="scroll-section" id="apidesign">
      <div className="scroll-left">
        <div className="chapter-num">Chapter 13 · LLD / HLD</div>
        <h2 className="chapter-title">API Design</h2>
        <div className="chapter-body">
          <p>Every system boundary is an API. Getting the design right determines how easy it is to evolve, version, and consume your system.</p>
          <p>Three dominant styles:</p>
          <ul style={{ margin:"12px 0 16px 16px", lineHeight:2.2, fontSize:14 }}>
            <li><strong>REST</strong> — resource-based, HTTP verbs, JSON, stateless</li>
            <li><strong>gRPC</strong> — contract-first, binary Protobuf, HTTP/2, streaming</li>
            <li><strong>GraphQL</strong> — query language, client-defined shape, single endpoint</li>
          </ul>
          <div className="highlight-box">REST for public APIs. gRPC for internal microservice communication. GraphQL for complex client-driven data requirements (mobile, BFF pattern).</div>
          <p>Regardless of style, good API design requires: <strong>idempotency</strong> (safe retries), <strong>pagination</strong> (cursor-based for large datasets), <strong>versioning</strong>, and <strong>clear error contracts</strong>.</p>
          <div className="note-box">Idempotency key: clients send a unique key per request. Server deduplicates — critical for payment APIs.</div>
        </div>
      </div>
      <div className="scroll-right">
        <FadePanel>
          <div className="panel-label">REST vs gRPC vs GraphQL</div>
          <table className="compare-table">
            <thead><tr><th>Factor</th><th>REST</th><th>gRPC</th><th>GraphQL</th></tr></thead>
            <tbody>
              {[
                ["Protocol",     "HTTP/1.1",       "HTTP/2",         "HTTP/1.1-2"],
                ["Payload",      "JSON (text)",    "Protobuf (bin)", "JSON"],
                ["Typing",       "Implicit",       "Strong (.proto)","Schema (SDL)"],
                ["Streaming",    "Limited",        "✓ Native",       "Subscriptions"],
                ["Caching",      "✓ HTTP cache",   "Custom",         "Complex"],
                ["Learning",     "Low",            "Medium",         "High"],
                ["Best for",     "Public APIs",    "Microservices",  "Flexible clients"],
              ].map(([f,r,g,q]) => <tr key={f}><td>{f}</td><td>{r}</td><td>{g}</td><td>{q}</td></tr>)}
            </tbody>
          </table>
        </FadePanel>
        <FadePanel>
          <div className="panel-label">Code Examples</div>
          <div className="stepper">
            {[["rest","🌐","REST"],["grpc","⚡","gRPC"],["graphql","🔷","GraphQL"]].map(([k,icon,label]) => (
              <button key={k} className={`step ${tab===k?"active":""}`} onClick={() => setTab(k)}>
                <span className="step-num">{icon}</span>{label}
              </button>
            ))}
          </div>
          <pre style={{ background:"#080c14", borderRadius:8, padding:"16px 18px", fontFamily:"DM Mono,monospace", fontSize:12, lineHeight:1.75, color:"#c5d1e8", overflowX:"auto", marginTop:16 }}>{code[tab]}</pre>
        </FadePanel>
      </div>
    </div>
  );
};

/* ─── CH 14: DNS & CDN ─── */
const Ch14 = () => (
  <div className="scroll-section" id="dnscdn">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 14 · HLD</div>
      <h2 className="chapter-title">DNS & CDN</h2>
      <div className="chapter-body">
        <p><span className="term">DNS (Domain Name System)</span> is the phone book of the internet — it translates human-readable hostnames like <code style={{color:"#4ff79e",fontSize:13}}>api.example.com</code> into IP addresses your machine can route to.</p>
        <p>DNS resolution follows a hierarchy: <strong>Browser cache → OS cache → Recursive resolver → Root nameserver → TLD nameserver → Authoritative nameserver</strong>. TTL (Time To Live) controls how long each answer is cached.</p>
        <div className="highlight-box">DNS is also a load balancing tool: return different IPs based on geography (GeoDNS), or rotate multiple IPs (round-robin DNS) for simple distribution.</div>
        <p>A <span className="term">CDN (Content Delivery Network)</span> is a globally distributed network of edge servers that cache static content close to users. Instead of every user hitting your origin server in Virginia, they hit a CDN edge node 10ms away.</p>
        <p>CDN strategies: <strong>Push CDN</strong> (you upload content proactively) vs <strong>Pull CDN</strong> (CDN fetches from origin on first miss, then caches). Pull CDNs like CloudFront and Cloudflare are more common.</p>
        <div className="note-box">Cache-Control headers control CDN TTL. Set long TTLs for versioned assets (bundle.abc123.js) and short/no-cache for HTML and APIs.</div>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">DNS Resolution Flow</div>
        <svg viewBox="0 0 560 220" width="100%" height="220">
          <Defs />
          <Box x={10}  y={90} w={80}  h={44} variant="primary"   />
          <TL  x={50}  y={110} text="Browser"  color="#4f8ef7" size={10} />
          <TL  x={50}  y={124} text="api.ex.com" size={9} color="#3d4560" />
          <Box x={130} y={90} w={90}  h={44} variant="secondary"  />
          <TL  x={175} y={110} text="Recursive"  size={10} />
          <TL  x={175} y={124} text="Resolver"   size={9} color="#3d4560" />
          <Box x={260} y={20}  w={90}  h={44} variant="muted"     />
          <TL  x={305} y={40}  text="Root NS"    size={10} color="#e8ecf4" />
          <TL  x={305} y={54}  text=". (13 roots)" size={9} color="#3d4560" />
          <Box x={260} y={90}  w={90}  h={44} variant="muted"     />
          <TL  x={305} y={110} text="TLD NS"     size={10} color="#e8ecf4" />
          <TL  x={305} y={124} text=".com"       size={9} color="#3d4560" />
          <Box x={260} y={160} w={90}  h={44} variant="muted"     />
          <TL  x={305} y={180} text="Auth NS"    size={10} color="#e8ecf4" />
          <TL  x={305} y={194} text="example.com" size={9} color="#3d4560" />
          <Box x={400} y={90}  w={140} h={44} variant="success"   />
          <TL  x={470} y={108} text="93.184.216.34" color="#4ff79e" size={10} />
          <TL  x={470} y={124} text="← IP returned" size={9} color="#3d4560" />
          <NL x1={90}  y1={112} x2={128} y2={112} color="arr-b" />
          <NL x1={175} y1={90}  x2={280} y2={42}  color="arr"   />
          <NL x1={305} y1={64}  x2={305} y2={88}  color="arr"   />
          <NL x1={305} y1={134} x2={305} y2={158} color="arr"   />
          <NL x1={350} y1={182} x2={398} y2={112} color="arr-g" />
        </svg>
      </FadePanel>
      <FadePanel>
        <div className="panel-label">CDN Pull Architecture</div>
        <svg viewBox="0 0 560 200" width="100%" height="200">
          <Defs />
          <Box x={220} y={10}  w={120} h={44} variant="secondary" />
          <TL  x={280} y={30}  text="Origin Server" size={10} />
          <TL  x={280} y={44}  text="(single region)" size={9} color="#3d4560" />
          {[["Edge US","#4f8ef7",10,120],["Edge EU","#4ff79e",160,120],["Edge Asia","#f7824f",310,120],["Edge SA","#f7d84f",460,120]].map(([label,color,x,y]) => (
            <g key={label}>
              <rect x={x} y={y} width={110} height={44} rx={6} fill={`${color}18`} stroke={color} strokeWidth={1} />
              <TL x={x+55} y={y+18} text={label} size={10} color={color} />
              <TL x={x+55} y={y+32} text="cached copy" size={9} color="#3d4560" />
              <NL x1={280} y1={54} x2={x+55} y2={y} color="arr" dash />
            </g>
          ))}
          <TL x={280} y={190} text="Users hit nearest edge node — 10ms vs 200ms to origin" size={9} color="#3d4560" />
        </svg>
      </FadePanel>
    </div>
  </div>
);

/* ─── CH 15: CONSISTENT HASHING & RATE LIMITING ─── */
const Ch15 = () => (
  <div className="scroll-section" id="hashing">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 15 · HLD</div>
      <h2 className="chapter-title">Consistent Hashing & Rate Limiting</h2>
      <div className="chapter-body">
        <p><span className="term">Consistent hashing</span> solves a fundamental problem: when you add or remove a server from a distributed cache or database cluster, how do you minimize data movement?</p>
        <p>In naive modular hashing (<code style={{color:"#4ff79e",fontSize:13}}>server = hash(key) % N</code>), changing N remaps almost every key. Consistent hashing maps both keys and servers onto a ring — adding/removing a server only moves ~1/N of keys.</p>
        <div className="highlight-box">Used in: Amazon DynamoDB, Apache Cassandra, Redis Cluster, CDN routing, and every distributed cache that needs hot-spot-free rebalancing.</div>
        <p><strong>Virtual nodes</strong> (vnodes) place each physical server at multiple positions on the ring, ensuring more even distribution even with heterogeneous hardware.</p>
        <p><strong>Rate limiting</strong> protects your system from abuse and overload. Key algorithms:</p>
        <ul style={{ margin:"12px 0 16px 16px", lineHeight:2.2, fontSize:14 }}>
          <li><strong>Token Bucket</strong> — tokens refill at fixed rate; burst allowed</li>
          <li><strong>Leaky Bucket</strong> — requests drain at fixed rate; smooths bursts</li>
          <li><strong>Fixed Window</strong> — simple counter per time window</li>
          <li><strong>Sliding Window Log</strong> — precise but memory-intensive</li>
        </ul>
        <div className="note-box">In practice: Token Bucket is the most widely deployed (used by AWS, Stripe). Redis with INCR + EXPIRE implements Fixed Window cheaply.</div>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">Consistent Hashing Ring</div>
        <svg viewBox="0 0 560 260" width="100%" height="260">
          <Defs />
          <circle cx={280} cy={130} r={100} fill="none" stroke="#252a3a" strokeWidth={2} />
          <TL x={280} y={134} text="Hash Ring" size={10} color="#3d4560" />
          <TL x={280} y={148} text="0 ————— 2³²" size={9} color="#3d4560" />
          {[
            {angle:-60, label:"Server A", color:"#4f8ef7", vn:"vA1"},
            {angle:60,  label:"Server B", color:"#4ff79e", vn:"vB1"},
            {angle:170, label:"Server C", color:"#f7824f", vn:"vC1"},
            {angle:-160,label:"Server A", color:"#4f8ef7", vn:"vA2"},
            {angle:20,  label:"Server B", color:"#4ff79e", vn:"vB2"},
            {angle:130, label:"Server C", color:"#f7824f", vn:"vC2"},
          ].map(({angle, label, color, vn}) => {
            const r = 100, cx=280, cy=130;
            const rad = (angle * Math.PI) / 180;
            const x = cx + r * Math.cos(rad), y = cy + r * Math.sin(rad);
            const lx = cx + (r+28) * Math.cos(rad), ly = cy + (r+28) * Math.sin(rad);
            return (
              <g key={vn}>
                <circle cx={x} cy={y} r={8} fill={`${color}30`} stroke={color} strokeWidth={1.5} />
                <text x={lx} y={ly+4} fontFamily="DM Mono,monospace" fontSize={9} fill={color} textAnchor="middle">{label}</text>
                <text x={lx} y={ly+14} fontFamily="DM Mono,monospace" fontSize={8} fill="#3d4560" textAnchor="middle">({vn})</text>
              </g>
            );
          })}
          {/* key dots */}
          {[{angle:0,label:"key1"},{angle:100,label:"key2"},{angle:220,label:"key3"}].map(({angle,label}) => {
            const r=100,cx=280,cy=130,rad=(angle*Math.PI)/180;
            const x=cx+r*Math.cos(rad),y=cy+r*Math.sin(rad);
            return <g key={label}><circle cx={x} cy={y} r={4} fill="#f7d84f" /><text x={x+8} y={y+4} fontFamily="DM Mono,monospace" fontSize={8} fill="#f7d84f">{label}</text></g>;
          })}
          <TL x={280} y={250} text="Each key routes clockwise to nearest server node" size={9} color="#3d4560" />
        </svg>
      </FadePanel>
      <FadePanel>
        <div className="panel-label">Token Bucket Rate Limiter</div>
        <pre style={{ background:"#080c14", borderRadius:8, padding:"16px 18px", fontFamily:"DM Mono,monospace", fontSize:12, lineHeight:1.75, color:"#c5d1e8", overflowX:"auto" }}>
{`class TokenBucket {
  private tokens: number;
  private lastRefill: number = Date.now();

  constructor(
    private capacity: number,   // max burst
    private refillRate: number, // tokens per second
  ) { this.tokens = capacity; }

  allow(): boolean {
    this.refill();
    if (this.tokens >= 1) {
      this.tokens--;
      return true;  // ✓ allow request
    }
    return false;   // ✗ rate limited (429)
  }

  private refill() {
    const now = Date.now();
    const elapsed = (now - this.lastRefill) / 1000;
    this.tokens = Math.min(
      this.capacity,
      this.tokens + elapsed * this.refillRate
    );
    this.lastRefill = now;
  }
}
// Redis implementation: INCR key, EXPIRE key window`}
        </pre>
      </FadePanel>
    </div>
  </div>
);

/* ─── CH 16: REPLICATION & DISTRIBUTED TRANSACTIONS ─── */
const Ch16 = () => (
  <div className="scroll-section" id="replication">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 16 · HLD</div>
      <h2 className="chapter-title">Replication & Distributed Transactions</h2>
      <div className="chapter-body">
        <p><span className="term">Replication</span> copies data to multiple nodes for fault tolerance and read scaling. Three strategies:</p>
        <ul style={{ margin:"12px 0 16px 16px", lineHeight:2.2, fontSize:14 }}>
          <li><strong>Single-leader</strong> — all writes go to one leader, replicated to followers (MySQL, Postgres)</li>
          <li><strong>Multi-leader</strong> — multiple nodes accept writes; needs conflict resolution</li>
          <li><strong>Leaderless</strong> — any node accepts writes; uses quorum reads/writes (Cassandra, Dynamo)</li>
        </ul>
        <div className="highlight-box">Replication lag is real. A follower might be seconds behind the leader — reads from followers can return stale data. This is the "read-your-writes" consistency problem.</div>
        <p>In microservices, <strong>distributed transactions</strong> (spanning multiple DBs) can't use a traditional ACID commit. Two approaches:</p>
        <ul style={{ margin:"12px 0 16px 16px", lineHeight:2.2, fontSize:14 }}>
          <li><strong>2PC (Two-Phase Commit)</strong> — coordinator asks all participants to prepare, then commit. Blocking if coordinator crashes.</li>
          <li><strong>SAGA pattern</strong> — sequence of local transactions, each publishing an event. Compensating transactions for rollback.</li>
        </ul>
        <div className="note-box">SAGA is the modern standard. Two flavours: Choreography (services react to events) and Orchestration (a central saga orchestrator directs steps).</div>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">Leader-Follower Replication</div>
        <svg viewBox="0 0 560 200" width="100%" height="200">
          <Defs />
          <Box x={200} y={20} w={160} h={50} variant="primary" />
          <TL x={280} y={42} text="Leader (Primary)" color="#4f8ef7" size={11} />
          <TL x={280} y={58} text="Accepts all WRITEs" size={9} color="#3d4560" />
          {[["Follower 1","#4ff79e",20,130],["Follower 2","#4ff79e",210,130],["Follower 3","#4ff79e",400,130]].map(([label,color,x,y]) => (
            <g key={label}>
              <Box x={x} y={y} w={130} h={44} variant="success" rx={6} />
              <TL x={x+65} y={y+18} text={label} size={10} />
              <TL x={x+65} y={y+32} text="READ replica" size={9} color="#3d4560" />
              <NL x1={280} y1={70} x2={x+65} y2={y} color="arr-g" dash label="async repl" lx={(280+x+65)/2} ly={(70+y)/2-5} />
            </g>
          ))}
          <TL x={280} y={192} text="Writes: leader only  |  Reads: distribute across followers" size={9} color="#3d4560" />
        </svg>
      </FadePanel>
      <FadePanel>
        <div className="panel-label">SAGA Pattern — Choreography vs Orchestration</div>
        <svg viewBox="0 0 560 230" width="100%" height="230">
          <Defs />
          <TL x={140} y={16} text="Choreography" color="#f7d84f" size={11} />
          {[["OrderSvc","#4f8ef7",20,30],["PaymentSvc","#4ff79e",20,90],["InventorySvc","#f7824f",20,150]].map(([label,color,x,y]) => (
            <g key={label}><Box x={x} y={y} w={130} h={38} variant={color==="#4f8ef7"?"primary":color==="#4ff79e"?"success":"secondary"} rx={5} /><TL x={x+65} y={y+23} text={label} size={10} color={color} /></g>
          ))}
          <Box x={170} y={30}  w={80} h={38} variant="yellow" rx={5}/><TL x={210} y={53} text="EventBus" size={9} color="#f7d84f"/>
          <NL x1={150} y1={49} x2={168} y2={49} color="arr-y" />
          <NL x1={210} y1={68} x2={150} y2={91} color="arr-g" dash />
          <NL x1={150} y1={109} x2={210} y2={109} color="arr-o" />
          <NL x1={210} y1={68} x2={150} y2={151} color="arr-o" dash />

          <TL x={420} y={16} text="Orchestration" color="#4ff79e" size={11} />
          <Box x={370} y={30} w={110} h={38} variant="yellow" rx={5}/>
          <TL x={425} y={53} text="Saga Orchestrator" size={9} color="#f7d84f"/>
          {[["OrderSvc","#4f8ef7",310,90],["PaymentSvc","#4ff79e",400,140],["InvSvc","#f7824f",490,90]].map(([label,color,x,y]) => (
            <g key={label}><Box x={x} y={y} w={100} h={36} variant={color==="#4f8ef7"?"primary":color==="#4ff79e"?"success":"secondary"} rx={5}/><TL x={x+50} y={y+22} text={label} size={9} color={color}/><NL x1={425} y1={68} x2={x+50} y2={y} color="arr-y"/></g>
          ))}
          <TL x={280} y={220} text="Choreography: event-driven, decoupled  |  Orchestration: explicit, easier to debug" size={9} color="#3d4560" />
        </svg>
      </FadePanel>
    </div>
  </div>
);

/* ─── CH 17: SECURITY ─── */
const Ch17 = () => {
  const [tab, setTab] = useState("jwt");
  const code = {
    jwt:`// JWT Structure: Header.Payload.Signature
// Header
{ "alg": "HS256", "typ": "JWT" }
// Payload
{
  "sub": "user_42",
  "name": "Alice",
  "role": "admin",
  "iat": 1700000000,   // issued at
  "exp": 1700003600    // expires in 1h
}
// Signature = HMAC_SHA256(base64(header) + "." + base64(payload), SECRET)

// Server validation:
// 1. Decode header + payload (base64)
// 2. Recompute signature with SECRET
// 3. Compare — tamper-proof
// 4. Check exp hasn't passed
// NEVER store sensitive data in payload — it's only base64, not encrypted`,
    oauth:`// OAuth 2.0 Authorization Code Flow
// 1. User clicks "Login with Google"
// 2. App redirects to Google:
//    GET https://accounts.google.com/o/oauth2/auth
//      ?client_id=YOUR_ID
//      &redirect_uri=https://app.com/callback
//      &scope=email profile
//      &response_type=code
//      &state=RANDOM_STRING  ← CSRF protection

// 3. User approves → Google redirects back:
//    GET https://app.com/callback?code=AUTH_CODE

// 4. App exchanges code for tokens (server-side):
POST https://oauth2.googleapis.com/token
  { code, client_id, client_secret, grant_type }
  ← { access_token, id_token, refresh_token }

// 5. Use access_token to call Google APIs
// 6. Use refresh_token to get new access_token when expired`,
  };
  return (
    <div className="scroll-section" id="security">
      <div className="scroll-left">
        <div className="chapter-num">Chapter 17 · HLD</div>
        <h2 className="chapter-title">Security — Auth, JWT & OAuth2</h2>
        <div className="chapter-body">
          <p>Security is non-negotiable. The two concerns that appear in every system design:</p>
          <p><span className="term">Authentication</span> — who are you? Verify identity via password, biometric, or token.</p>
          <p><span className="term">Authorization</span> — what can you do? Check permissions after identity is confirmed.</p>
          <p><strong>Session vs Token-based auth:</strong></p>
          <ul style={{ margin:"12px 0 16px 16px", lineHeight:2.2, fontSize:14 }}>
            <li><strong>Session (stateful)</strong> — server stores session in DB/Redis; client sends session ID cookie. Simple but doesn't scale horizontally without shared session store.</li>
            <li><strong>JWT (stateless)</strong> — all claims in signed token; server verifies signature without DB lookup. Scales perfectly but can't invalidate before expiry.</li>
          </ul>
          <div className="highlight-box">JWT best practices: short expiry (15 min access token) + refresh token rotation. Store refresh tokens in HttpOnly cookies, not localStorage.</div>
          <p><strong>OAuth 2.0</strong> is an authorization framework — it lets users grant third-party apps access to their data without sharing credentials ("Login with Google").</p>
          <div className="note-box">Common attacks: SQL injection (use parameterized queries), XSS (escape output, CSP headers), CSRF (SameSite cookies, state parameter in OAuth), SSRF (validate URLs server makes requests to).</div>
        </div>
      </div>
      <div className="scroll-right">
        <FadePanel>
          <div className="panel-label">Authentication Flow Comparison</div>
          <svg viewBox="0 0 560 180" width="100%" height="180">
            <Defs />
            <TL x={130} y={14} text="Session-based (stateful)" color="#f7824f" size={11} />
            <Box x={10}  y={24} w={60} h={36} variant="primary" rx={4}/><TL x={40} y={46} text="Client" size={9} color="#4f8ef7"/>
            <Box x={100} y={24} w={80} h={36} variant="secondary" rx={4}/><TL x={140} y={46} text="Server" size={9}/>
            <Box x={210} y={24} w={80} h={36} variant="muted" rx={4}/><TL x={250} y={46} text="Session DB" size={9} color="#e8ecf4"/>
            <NL x1={70} y1={42} x2={98} y2={42} color="arr-b" label="login" lx={84} ly={36}/>
            <NL x1={180} y1={42} x2={208} y2={42} color="arr" label="store" lx={194} ly={36}/>
            <NL x1={208} y1={52} x2={180} y2={52} color="arr-o" label="session_id" lx={194} ly={62}/>
            <NL x1={98} y1={52} x2={70} y2={52} color="arr-o" label="cookie" lx={84} ly={62}/>
            <TL x={140} y={90} text="Each request: lookup session_id in DB" size={9} color="#3d4560"/>

            <TL x={420} y={14} text="JWT (stateless)" color="#4ff79e" size={11}/>
            <Box x={310} y={24} w={60} h={36} variant="primary" rx={4}/><TL x={340} y={46} text="Client" size={9} color="#4f8ef7"/>
            <Box x={400} y={24} w={80} h={36} variant="success" rx={4}/><TL x={440} y={46} text="Server" size={9}/>
            <NL x1={370} y1={42} x2={398} y2={42} color="arr-b" label="login" lx={384} ly={36}/>
            <NL x1={398} y1={52} x2={370} y2={52} color="arr-g" label="JWT token" lx={384} ly={62}/>
            <TL x={420} y={90} text="Each request: verify signature only (no DB)" size={9} color="#3d4560"/>
            <Box x={310} y={110} w={230} h={54} variant="success" rx={6}/>
            <TL x={425} y={128} text="Header.Payload.Signature" color="#4ff79e" size={10}/>
            <TL x={425} y={144} text="Stateless · Scalable · No DB lookup" size={9} color="#3d4560"/>
          </svg>
        </FadePanel>
        <FadePanel>
          <div className="panel-label">JWT & OAuth2 Code</div>
          <div className="stepper">
            {[["jwt","🔑","JWT"],["oauth","🌐","OAuth 2.0"]].map(([k,icon,label]) => (
              <button key={k} className={`step ${tab===k?"active":""}`} onClick={() => setTab(k)}>
                <span className="step-num">{icon}</span>{label}
              </button>
            ))}
          </div>
          <pre style={{ background:"#080c14", borderRadius:8, padding:"16px 18px", fontFamily:"DM Mono,monospace", fontSize:12, lineHeight:1.75, color:"#c5d1e8", overflowX:"auto", marginTop:16 }}>{code[tab]}</pre>
        </FadePanel>
      </div>
    </div>
  );
};

/* ─── CH 18: OBSERVABILITY ─── */
const Ch18 = () => (
  <div className="scroll-section" id="observability">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 18 · HLD</div>
      <h2 className="chapter-title">Observability</h2>
      <div className="chapter-body">
        <p>You can't fix what you can't see. <span className="term">Observability</span> is the ability to understand your system's internal state from its external outputs. It has three pillars:</p>
        <div className="timeline">
          {[
            { dot:"",       title:"Logs — What happened",     desc:"Structured, timestamped records of discrete events. Use JSON logs with correlation IDs. Ship to ELK (Elasticsearch + Logstash + Kibana) or Loki." },
            { dot:"orange", title:"Metrics — How it's doing",  desc:"Numeric measurements over time: request rate, error rate, latency percentiles (p50/p95/p99), CPU/memory. Prometheus + Grafana is the standard stack." },
            { dot:"green",  title:"Traces — Why it's slow",   desc:"Follow a request across all microservices. Each service adds a span. Visualise the full call tree to find the slow hop. Jaeger, Zipkin, OpenTelemetry." },
          ].map(({ dot, title, desc }) => (
            <div className="tl-item" key={title}>
              <div className={`tl-dot${dot?" "+dot:""}`} />
              <div className="tl-title">{title}</div>
              <div className="tl-desc">{desc}</div>
            </div>
          ))}
        </div>
        <div className="highlight-box">The four golden signals (Google SRE): Latency, Traffic, Errors, Saturation. Alert on these before users notice.</div>
        <p><strong>SLI / SLO / SLA:</strong> Service Level Indicator (measured metric), Service Level Objective (target, e.g. 99.9% uptime), Service Level Agreement (contractual commitment with penalties).</p>
        <div className="note-box">Always add a correlation/trace ID to every request at the ingress point (API gateway) and propagate it through all downstream calls. Log everything with this ID.</div>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">Observability Stack</div>
        <svg viewBox="0 0 560 220" width="100%" height="220">
          <Defs />
          <Box x={200} y={10} w={160} h={44} variant="primary"/>
          <TL x={280} y={30} text="Your Application" color="#4f8ef7" size={11}/>
          <TL x={280} y={44} text="instruments all three signals" size={9} color="#3d4560"/>
          {[
            {x:20,  y:110, w:140, label:"Logs",    sub:"ELK / Loki",      color:"#4f8ef7", variant:"primary"},
            {x:210, y:110, w:140, label:"Metrics",  sub:"Prometheus+Grafana",color:"#f7824f",variant:"secondary"},
            {x:400, y:110, w:140, label:"Traces",   sub:"Jaeger / OTEL",   color:"#4ff79e", variant:"success"},
          ].map(({x,y,w,label,sub,color,variant}) => (
            <g key={label}>
              <Box x={x} y={y} w={w} h={54} variant={variant} rx={8}/>
              <TL x={x+w/2} y={y+22} text={label}  size={11} color={color}/>
              <TL x={x+w/2} y={y+38} text={sub}    size={9}  color="#3d4560"/>
              <NL x1={280} y1={54} x2={x+w/2} y2={y} color="arr-b" dash/>
            </g>
          ))}
          {[
            {x:20,  y:186, w:140, label:"Kibana / Grafana Loki", color:"#4f8ef7"},
            {x:210, y:186, w:140, label:"Grafana Dashboards",    color:"#f7824f"},
            {x:400, y:186, w:140, label:"Jaeger UI / Tempo",     color:"#4ff79e"},
          ].map(({x,y,w,label,color}) => (
            <g key={label}>
              <rect x={x} y={y} width={w} height={30} rx={5} fill={`${color}10`} stroke={`${color}40`} strokeWidth={1}/>
              <TL x={x+w/2} y={y+19} text={label} size={9} color={color}/>
              <line x1={x+w/2} y1={y} x2={x+w/2} y2={164} stroke={`${color}50`} strokeWidth={1} strokeDasharray="3 2"/>
            </g>
          ))}
        </svg>
      </FadePanel>
      <FadePanel>
        <div className="panel-label">Distributed Trace Example</div>
        <pre style={{ background:"#080c14", borderRadius:8, padding:"16px 18px", fontFamily:"DM Mono,monospace", fontSize:12, lineHeight:1.75, color:"#c5d1e8", overflowX:"auto" }}>
{`TraceID: abc-123   Total: 340ms

├─ API Gateway          [0ms  → 5ms  ]  5ms
│
├─ UserService          [5ms  → 45ms ]  40ms
│   └─ Postgres query   [10ms → 40ms ]  30ms  ← slow!
│
├─ OrderService         [45ms → 120ms]  75ms
│   ├─ Redis GET        [46ms → 47ms ]  1ms
│   └─ Postgres query   [48ms → 118ms]  70ms  ← slow!
│
├─ PaymentService       [120ms→ 310ms]  190ms ← bottleneck
│   └─ Stripe API call  [125ms→ 305ms]  180ms ← external
│
└─ NotifService (async) [310ms→ 340ms]  30ms
   └─ Email via SES     [async]

// Fix: cache repeated DB queries, async payment processing`}
        </pre>
      </FadePanel>
    </div>
  </div>
);

/* ─── CH 19: REMAINING GOF PATTERNS ─── */
const Ch19 = () => {
  const groups = {
    creational: [
      { name:"Prototype",       emoji:"🧬", desc:"Clone an existing object instead of creating from scratch. Useful when construction is expensive. JavaScript's object spread is a shallow prototype.", example:"Object.assign({}, existingConfig, overrides)" },
      { name:"Abstract Factory", emoji:"🏗️", desc:"Create families of related objects without specifying concrete classes. e.g., UIFactory with createButton() + createCheckbox() — one factory for Windows, one for Mac.", example:"const ui = new WindowsUIFactory(); ui.createButton(); ui.createCheckbox();" },
    ],
    structural: [
      { name:"Proxy",    emoji:"🪞", desc:"Provide a substitute that controls access to another object. Uses: lazy loading (virtual proxy), access control (protection proxy), caching (cache proxy), logging.", example:"class ImageProxy { load() { if (!this.real) this.real = new HeavyImage(url); return this.real; }}" },
      { name:"Bridge",   emoji:"🌉", desc:"Decouple an abstraction from its implementation so both can vary independently. e.g., Shape (abstraction) + Renderer (implementation) — combine Circle+SVG or Square+Canvas freely.", example:"new Circle(new SVGRenderer()); new Square(new CanvasRenderer());" },
      { name:"Composite",emoji:"🌳", desc:"Compose objects into tree structures. Treat individual objects and compositions uniformly. e.g., File and Folder both implement Component with getSize().", example:"folder.add(file1); folder.add(subFolder); folder.getSize(); // recursive" },
      { name:"Flyweight", emoji:"🪶", desc:"Share common state among many fine-grained objects to reduce memory. e.g., a text editor stores character style objects shared across all characters with that style.", example:"CharacterFactory.get('A', bold) // returns cached flyweight" },
    ],
    behavioral: [
      { name:"Template Method", emoji:"📋", desc:"Define the skeleton of an algorithm in a base class; let subclasses override specific steps without changing the overall structure.", example:"class Report { generate() { this.header(); this.body(); this.footer(); } }" },
      { name:"State",           emoji:"🔄", desc:"Allow an object to alter its behaviour when its internal state changes. Looks like the object changed its class. e.g., a vending machine behaves differently when idle, selected, dispensing.", example:"machine.setState(new IdleState()); machine.insertCoin(); // transitions to SelectedState" },
      { name:"Iterator",        emoji:"🔁", desc:"Provide a way to sequentially access elements of a collection without exposing underlying representation. Every for...of loop in JS uses an iterator.", example:"for (const item of myCustomCollection) { process(item); }" },
      { name:"Mediator",        emoji:"🎙️", desc:"Reduce chaotic dependencies by making components communicate through a mediator object. e.g., an air traffic controller coordinates planes without them talking directly.", example:"chatRoom.send(alice, 'Hi all'); // room mediates to all members" },
      { name:"Memento",         emoji:"💾", desc:"Capture and restore an object's internal state without violating encapsulation. Powers undo/redo in editors and rollback in games.", example:"const snapshot = editor.save(); editor.restore(snapshot);" },
      { name:"Visitor",         emoji:"🚶", desc:"Add operations to objects without modifying them. Define a new operation by creating a new visitor class. e.g., adding a tax calculator to an existing AST node hierarchy.", example:"ast.accept(new TypeChecker()); ast.accept(new CodeGenerator());" },
      { name:"Chain of Responsibility", emoji:"⛓️", desc:"Pass a request along a chain of handlers. Each handler decides to process it or pass it on. e.g., Express middleware, HR approval chains, support escalation.", example:"request.handle(auth → rateLimiter → logger → controller)" },
    ],
  };
  const [group, setGroup] = useState("creational");
  return (
    <div className="scroll-section" id="patterns2">
      <div className="scroll-left">
        <div className="chapter-num">Chapter 19 · LLD</div>
        <h2 className="chapter-title">Remaining GoF Patterns</h2>
        <div className="chapter-body">
          <p>The original Gang of Four book documents 23 patterns. Chapter 03 covered 8 essential ones. Here are the remaining 15 — equally important for senior-level interviews and production code.</p>
          <div className="stepper">
            {[["creational","🏗️","Creational (2)"],["structural","🔗","Structural (4)"],["behavioral","🧩","Behavioral (7)"]].map(([k,icon,label]) => (
              <button key={k} className={`step ${group===k?"active":""}`} onClick={() => setGroup(k)}>
                <span className="step-num">{icon}</span>{label}
              </button>
            ))}
          </div>
          <div className="highlight-box">Every middleware chain (Express, Koa, Django) is Chain of Responsibility. Every for...of loop is Iterator. Every undo/redo is Memento or Command. Patterns are everywhere.</div>
          <div className="note-box">Don't force patterns. Recognise when a pattern emerges naturally from a problem — that's when it adds clarity, not complexity.</div>
        </div>
      </div>
      <div className="scroll-right">
        {groups[group].map((p) => (
          <FadePanel key={p.name}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
              <span style={{ fontSize:28 }}>{p.emoji}</span>
              <div>
                <div style={{ fontFamily:"'Playfair Display',serif", fontWeight:700, fontSize:17, color:"#fff", marginBottom:6 }}>{p.name}</div>
                <div style={{ fontSize:14, color:"#7a859e", lineHeight:1.75, marginBottom:12 }}>{p.desc}</div>
                <pre style={{ background:"#080c14", borderRadius:6, padding:"10px 14px", fontFamily:"DM Mono,monospace", fontSize:11, lineHeight:1.65, color:"#c5d1e8", overflowX:"auto" }}>{p.example}</pre>
              </div>
            </div>
          </FadePanel>
        ))}
      </div>
    </div>
  );
};

/* ─── CH 20: LLD PRACTICE PROBLEMS ─── */
const Ch20 = () => {
  const [active, setActive] = useState("parking");
  const problems = {
    parking: {
      title:"Parking Lot", emoji:"🚗",
      entities:["ParkingLot","ParkingFloor","ParkingSpot","Vehicle (Car/Bike/Truck)","Ticket","Payment","DisplayBoard"],
      keyDecisions:[
        "ParkingSpot has SpotType (COMPACT/LARGE/HANDICAPPED) and isOccupied flag",
        "Vehicle hierarchy: abstract Vehicle → Car / Bike / Truck",
        "Strategy pattern for pricing: hourly, flat-rate, or free-first-hour",
        "Observer: DisplayBoard subscribes to spot availability changes",
      ],
      code:`class ParkingLot {          // Singleton
  private floors: ParkingFloor[];

  getAvailableSpot(v: Vehicle): ParkingSpot | null {
    for (const floor of this.floors) {
      const spot = floor.findSpot(v.getType());
      if (spot) return spot;
    }
    return null;
  }
}

class ParkingSpot {
  constructor(
    public id: string,
    public type: SpotType,
    private occupied = false,
  ) {}

  park(v: Vehicle): Ticket {
    this.occupied = true;
    return new Ticket(this, v, Date.now());
  }
  free(): void { this.occupied = false; }
}`,
    },
    elevator: {
      title:"Elevator System", emoji:"🛗",
      entities:["ElevatorController","Elevator","ElevatorDoor","Request (Floor+Direction)","ElevatorButton","DisplayPanel"],
      keyDecisions:[
        "State pattern: IDLE / MOVING_UP / MOVING_DOWN / MAINTENANCE",
        "Strategy for scheduling: FCFS, SCAN (disk scheduling), LOOK algorithm",
        "Observer: buttons notify controller, controller assigns nearest elevator",
        "One request queue per elevator; controller dispatches optimally",
      ],
      code:`class ElevatorController {
  private elevators: Elevator[];

  dispatch(floor: number, dir: Direction): void {
    const best = this.findBest(floor, dir);
    best.addRequest(new Request(floor, dir));
  }

  private findBest(floor: number, dir: Direction) {
    // Find elevator already moving toward floor
    // then idle elevators, then any elevator
    return this.elevators
      .sort((a, b) => a.score(floor, dir) - b.score(floor, dir))[0];
  }
}

class Elevator {
  private state: ElevatorState = new IdleState(this);
  private requests: Request[] = [];

  setState(s: ElevatorState) { this.state = s; }
  step() { this.state.handle(); }
}`,
    },
    bookmyshow: {
      title:"BookMyShow", emoji:"🎬",
      entities:["Movie","Theatre","Screen","Show","Seat","Booking","Payment","User","Notification"],
      keyDecisions:[
        "Seat locking: optimistic lock (version field) or 10-min Redis TTL reservation",
        "Show → Screen → Seat hierarchy; each Show has its own SeatAvailability map",
        "Command pattern: BookingCommand with execute() and compensate() for SAGA",
        "Race condition: two users booking same seat — DB unique constraint + retry",
      ],
      code:`class BookingService {
  async book(userId: string, showId: string, seatIds: string[]) {
    // 1. Lock seats (Redis SET NX with 10-min TTL)
    const locked = await this.lockSeats(seatIds);
    if (!locked) throw new SeatUnavailableError();

    // 2. Create pending booking
    const booking = await this.db.createBooking({
      userId, showId, seatIds, status: 'PENDING'
    });

    // 3. Process payment
    try {
      await this.paymentSvc.charge(userId, booking.amount);
      await this.db.confirmBooking(booking.id);
      await this.notifSvc.sendConfirmation(userId, booking);
    } catch {
      await this.db.cancelBooking(booking.id);
      await this.unlockSeats(seatIds);   // compensating tx
      throw new PaymentFailedError();
    }
  }
}`,
    },
  };
  const p = problems[active];
  return (
    <div className="scroll-section" id="lldproblems">
      <div className="scroll-left">
        <div className="chapter-num">Chapter 20 · LLD</div>
        <h2 className="chapter-title">LLD Practice Problems</h2>
        <div className="chapter-body">
          <p>LLD interviews ask you to design a real-world system at the class level. The interviewer evaluates: entity identification, relationship design, pattern usage, and extensibility.</p>
          <p>Select a problem:</p>
          <div style={{ display:"flex", flexDirection:"column", gap:8, margin:"16px 0" }}>
            {Object.entries(problems).map(([k, prob]) => (
              <button key={k} onClick={() => setActive(k)} style={{ background: active===k?"rgba(79,142,247,.12)":"#141720", border:`1px solid ${active===k?"#4f8ef7":"#252a3a"}`, borderRadius:8, padding:"12px 16px", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:20 }}>{prob.emoji}</span>
                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:14, color: active===k?"#4f8ef7":"#e8ecf4", fontWeight:500 }}>{prob.title}</span>
              </button>
            ))}
          </div>
          <div className="highlight-box">Interview approach: 1) List entities 2) Define attributes + methods 3) Draw relationships 4) Identify patterns 5) Handle edge cases</div>
        </div>
      </div>
      <div className="scroll-right">
        <FadePanel key={active}>
          <div className="panel-label">{p.emoji} {p.title} — Entities</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:20 }}>
            {p.entities.map(e => (
              <span key={e} style={{ fontFamily:"DM Mono,monospace", fontSize:11, background:"rgba(79,142,247,.1)", border:"1px solid rgba(79,142,247,.2)", color:"#4f8ef7", padding:"4px 10px", borderRadius:20 }}>{e}</span>
            ))}
          </div>
          <div className="panel-label">Key Design Decisions</div>
          <div className="timeline" style={{ marginBottom:20 }}>
            {p.keyDecisions.map((d,i) => (
              <div className="tl-item" key={i}>
                <div className={`tl-dot ${["","orange","green",""][i]}`} />
                <div className="tl-desc">{d}</div>
              </div>
            ))}
          </div>
          <div className="panel-label">Core Implementation</div>
          <pre style={{ background:"#080c14", borderRadius:8, padding:"16px 18px", fontFamily:"DM Mono,monospace", fontSize:12, lineHeight:1.75, color:"#c5d1e8", overflowX:"auto" }}>{p.code}</pre>
        </FadePanel>
      </div>
    </div>
  );
};

/* ─── CH 21: REAL-WORLD SYSTEMS ─── */
const Ch21 = () => {
  const [sys, setSys] = useState("youtube");
  const systems = {
    youtube: {
      title:"YouTube / Video Streaming", emoji:"▶️",
      scale:"500 hours of video uploaded/min · 5B+ views/day",
      components:[
        { label:"Upload Pipeline",   desc:"Client → API → Raw Storage (S3) → Transcoding Queue → FFmpeg workers → 4K/1080p/480p HLS chunks → CDN", color:"#4f8ef7" },
        { label:"Metadata Service",  desc:"Video title, tags, views, likes stored in MySQL. Video ID → CDN URL mapping in Redis. Denormalised for fast reads.", color:"#4ff79e" },
        { label:"Recommendation",    desc:"Offline ML pipeline (Apache Spark) trains model on watch history. Results cached in Redis per user. Updated every few hours.", color:"#f7824f" },
        { label:"CDN Strategy",      desc:"Popular videos pushed to all edge nodes. Long-tail videos pulled on-demand. Adaptive bitrate (ABR) switches quality based on bandwidth.", color:"#f7d84f" },
      ],
    },
    whatsapp: {
      title:"WhatsApp / Chat System", emoji:"💬",
      scale:"100B+ messages/day · 2B+ users",
      components:[
        { label:"Message Flow",      desc:"Client → WebSocket → Chat Server → Kafka → Delivery Service → WebSocket → Recipient. Messages persisted to Cassandra (NoSQL for time-series writes).", color:"#4f8ef7" },
        { label:"Online Presence",   desc:"User connects → publishes 'online' to Redis pub/sub → friends' chat servers notified via subscription. Heartbeat every 30s. Last seen stored in Redis.", color:"#4ff79e" },
        { label:"Message Delivery",  desc:"3 states: SENT (server received), DELIVERED (device received), READ (user opened). Each state stored as a DB update, fan-out to sender.", color:"#f7824f" },
        { label:"Group Messages",    desc:"Group members stored in separate service. Message fan-out: server sends to each member's chat server (or use Kafka topic per group).", color:"#f7d84f" },
      ],
    },
    uber: {
      title:"Uber / Ride Sharing", emoji:"🚕",
      scale:"25M trips/day · real-time location for millions of drivers",
      components:[
        { label:"Location Service",  desc:"Drivers send GPS every 4s → WebSocket → Location Service → Redis GeoHash (efficient proximity queries). Key: GEOADD drivers lon lat driverId.", color:"#4f8ef7" },
        { label:"Matching Engine",   desc:"Rider requests ride → find N nearest drivers within radius (Redis GEORADIUS) → rank by ETA (maps API) → send offer to best driver → wait for accept.", color:"#4ff79e" },
        { label:"Trip Service",      desc:"Once matched: create Trip in Postgres, push driver+rider to shared Kafka topic, both clients subscribe for real-time updates.", color:"#f7824f" },
        { label:"Surge Pricing",     desc:"Demand/supply ratio per hexagonal zone (H3 geospatial index). Pre-computed every minute by batch job. Multiplier stored in Redis per zone.", color:"#f7d84f" },
      ],
    },
    notification: {
      title:"Notification System", emoji:"🔔",
      scale:"Billions of push/email/SMS notifications/day",
      components:[
        { label:"Ingestion API",     desc:"Services call NotifAPI with {userId, type, template, data}. API validates, enriches with user preferences/channels, publishes to Kafka topic per channel.", color:"#4f8ef7" },
        { label:"Channel Workers",   desc:"Separate consumers for Email (SES/SendGrid), Push (FCM/APNs), SMS (Twilio). Each worker reads from its Kafka topic, calls provider API with retry.", color:"#4ff79e" },
        { label:"User Preferences",  desc:"Users configure do-not-disturb, channel opt-outs. Stored in Redis (fast) + DB (durable). Checked at ingestion before publishing to Kafka.", color:"#f7824f" },
        { label:"Deduplication",     desc:"Idempotency key (userId + templateId + windowHash) stored in Redis with 24h TTL. If key exists, drop notification. Prevents duplicate sends on retry.", color:"#f7d84f" },
      ],
    },
    search: {
      title:"Search Autocomplete", emoji:"🔍",
      scale:"Google: 8.5B searches/day · <100ms response",
      components:[
        { label:"Trie / Index",      desc:"Prefix trie stores top-K suggestions per prefix. Offline job builds trie from search logs weighted by frequency × recency. Serialised to Redis.", color:"#4f8ef7" },
        { label:"Query Service",     desc:"GET /suggest?q=app → look up prefix in Redis trie → return top 5. P99 target: 20ms. Cache popular prefixes in CDN with short TTL.", color:"#4ff79e" },
        { label:"Data Collection",   desc:"Every search logged to Kafka → Spark job aggregates hourly → updates trie in Redis. Trending searches have higher weight (exponential decay).", color:"#f7824f" },
        { label:"Filtering",         desc:"Adult content filter, personalisation (your recent searches first), spell correction (edit distance), language detection for locale-specific results.", color:"#f7d84f" },
      ],
    },
  };
  const s = systems[sys];
  return (
    <div className="scroll-section" id="systems">
      <div className="scroll-left">
        <div className="chapter-num">Chapter 21 · HLD</div>
        <h2 className="chapter-title">Real-World Systems</h2>
        <div className="chapter-body">
          <p>The best way to prepare for system design interviews is to study real systems. Each one exercises different trade-offs and techniques.</p>
          <p>Select a system to explore:</p>
          <div style={{ display:"flex", flexDirection:"column", gap:8, margin:"16px 0" }}>
            {Object.entries(systems).map(([k, sv]) => (
              <button key={k} onClick={() => setSys(k)} style={{ background: sys===k?"rgba(79,142,247,.12)":"#141720", border:`1px solid ${sys===k?"#4f8ef7":"#252a3a"}`, borderRadius:8, padding:"12px 16px", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:20 }}>{sv.emoji}</span>
                <span style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, color: sys===k?"#4f8ef7":"#e8ecf4", fontWeight:500 }}>{sv.title}</span>
              </button>
            ))}
          </div>
          <div className="note-box">Pattern: every large system has the same skeleton — API layer, one or more datastores, a cache, a queue, and a background worker. The interesting part is how they're wired together.</div>
        </div>
      </div>
      <div className="scroll-right">
        <FadePanel key={sys}>
          <div className="panel-label">{s.emoji} {s.title}</div>
          <div style={{ fontFamily:"DM Mono,monospace", fontSize:11, color:"#f7d84f", background:"rgba(247,216,79,.08)", border:"1px solid rgba(247,216,79,.2)", borderRadius:6, padding:"8px 14px", marginBottom:20 }}>⚡ Scale: {s.scale}</div>
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            {s.components.map(({ label, desc, color }) => (
              <div key={label} style={{ background:"#0d0f14", border:`1px solid ${color}25`, borderLeft:`3px solid ${color}`, borderRadius:8, padding:"14px 16px" }}>
                <div style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color, marginBottom:6, textTransform:"uppercase", letterSpacing:"0.08em" }}>{label}</div>
                <div style={{ fontSize:13, color:"#7a859e", lineHeight:1.75 }}>{desc}</div>
              </div>
            ))}
          </div>
        </FadePanel>
      </div>
    </div>
  );
};

/* ─── CH 22: CAPACITY ESTIMATION ─── */
const Ch22 = () => (
  <div className="scroll-section" id="capacity">
    <div className="scroll-left">
      <div className="chapter-num">Chapter 22 · HLD</div>
      <h2 className="chapter-title">Capacity Estimation</h2>
      <div className="chapter-body">
        <p><span className="term">Back-of-envelope estimation</span> is a mandatory part of every system design interview. It drives every architectural decision — database choice, number of servers, cache size, storage tier.</p>
        <p>Memorise these numbers:</p>
        <table className="compare-table" style={{ marginBottom:16 }}>
          <thead><tr><th>Operation</th><th>Latency</th></tr></thead>
          <tbody>
            {[["L1 cache ref","0.5 ns"],["L2 cache ref","7 ns"],["RAM access","100 ns"],["SSD read","100 µs"],["HDD seek","10 ms"],["Round trip same DC","0.5 ms"],["Round trip diff region","150 ms"],["Mutex lock/unlock","25 ns"]].map(([op,lat]) => (
              <tr key={op}><td>{op}</td><td style={{ fontFamily:"DM Mono,monospace", color:"#4f8ef7" }}>{lat}</td></tr>
            ))}
          </tbody>
        </table>
        <div className="highlight-box">Rule of 72: at X% growth/year, the system doubles in 72/X years. At 20% growth → doubles in 3.6 years. Design for 2× current load minimum.</div>
        <div className="note-box">Powers of 2: 1KB=10³, 1MB=10⁶, 1GB=10⁹, 1TB=10¹², 1PB=10¹⁵. 1 million seconds ≈ 11.5 days. 1 billion seconds ≈ 31.5 years.</div>
      </div>
    </div>
    <div className="scroll-right">
      <FadePanel>
        <div className="panel-label">Worked Example — Twitter Scale</div>
        <pre style={{ background:"#080c14", borderRadius:8, padding:"16px 18px", fontFamily:"DM Mono,monospace", fontSize:12, lineHeight:1.9, color:"#c5d1e8", overflowX:"auto" }}>
{`Given: 300M MAU, 50M DAU, 2 reads per tweet written

── WRITES ──────────────────────────────────────
Tweets/day      = 50M users × 2 tweets    = 100M/day
Tweets/sec      = 100M / 86,400           ≈ 1,160 QPS
Peak (3× avg)                             ≈ 3,500 QPS

── READS ───────────────────────────────────────
Read/write ratio = 200:1 (Twitter-like feeds)
Reads/sec        = 1,160 × 200            ≈ 232,000 QPS
→ This is why you need caching!

── STORAGE ─────────────────────────────────────
Tweet size       = 140 chars + metadata   ≈ 1 KB
Storage/day      = 100M × 1 KB            = 100 GB/day
Storage/5 years  = 100 GB × 365 × 5      ≈ 182 TB

── MEDIA ────────────────────────────────────────
10% tweets have images (avg 200 KB)
Media/day        = 10M × 200 KB          = 2 TB/day
→ Use object storage (S3), not DB

── BANDWIDTH ────────────────────────────────────
Incoming         = 100 GB / 86,400       ≈ 1.2 MB/s
Outgoing (reads) = 1.2 × 200            ≈ 240 MB/s
→ CDN is mandatory for static assets`}
        </pre>
      </FadePanel>
      <FadePanel>
        <div className="panel-label">Quick Estimation Framework</div>
        <div className="timeline">
          {[
            { dot:"",       title:"1. Users & Traffic",   desc:"DAU × actions/day ÷ 86,400 = base QPS. Apply 3× peak multiplier. Split read:write ratio." },
            { dot:"orange", title:"2. Storage",           desc:"Object size × writes/day × retention years. Add 20% overhead. Separate hot (SSD) and cold (HDD/S3) storage." },
            { dot:"green",  title:"3. Bandwidth",         desc:"Incoming = avg_size × write_QPS. Outgoing = avg_size × read_QPS. Outgoing usually dominates." },
            { dot:"",       title:"4. Servers",           desc:"read_QPS ÷ 1,000 = read servers (rough). Each server handles ~1k req/s at 50ms avg latency. Add load balancers." },
          ].map(({ dot, title, desc }) => (
            <div className="tl-item" key={title}>
              <div className={`tl-dot${dot?" "+dot:""}`} />
              <div className="tl-title">{title}</div>
              <div className="tl-desc">{desc}</div>
            </div>
          ))}
        </div>
      </FadePanel>
    </div>
  </div>
);

/* ─── UPDATED TOC ITEMS (full list) ─── */
const TOC_ITEMS_NEW = [
  { num:"01 — LLD", label:"Fundamentals",              sub:"OOP pillars, SOLID principles",                      type:"lld", id:"fundamentals" },
  { num:"02 — LLD", label:"Classes & Objects",         sub:"UML, relationships, composition",                   type:"lld", id:"lld" },
  { num:"03 — LLD", label:"Core Design Patterns",      sub:"Singleton, Factory, Observer, Strategy…",           type:"lld", id:"oop" },
  { num:"04 — HLD", label:"System Architecture",       sub:"Monolith vs Microservices",                         type:"hld", id:"hld" },
  { num:"05 — HLD", label:"Scalability",               sub:"Horizontal, vertical, load balancing",              type:"hld", id:"scalability" },
  { num:"06 — HLD", label:"Databases",                 sub:"SQL, NoSQL, sharding, replication, indexes",        type:"hld", id:"databases" },
  { num:"07 — HLD", label:"Caching",                   sub:"Strategies, eviction, Redis",                       type:"hld", id:"caching" },
  { num:"08 — HLD", label:"Messaging & Queues",        sub:"Kafka, async communication",                        type:"hld", id:"messaging" },
  { num:"09 — HLD", label:"Microservices",             sub:"Service mesh, API Gateway, resilience",             type:"hld", id:"microservices" },
  { num:"10 — HLD", label:"CAP Theorem",               sub:"Consistency, availability, partition",              type:"hld", id:"cap" },
  { num:"11 — HLD", label:"URL Shortener & Twitter",   sub:"Fan-out, Base62, cache-aside",                      type:"hld", id:"realworld" },
  { num:"12 — LLD", label:"Concurrency",               sub:"Mutex, semaphore, deadlock, thread pools",          type:"lld", id:"concurrency" },
  { num:"13 — LLD", label:"API Design",                sub:"REST vs gRPC vs GraphQL",                           type:"lld", id:"apidesign" },
  { num:"14 — HLD", label:"DNS & CDN",                 sub:"Resolution flow, edge caching, TTL",                type:"hld", id:"dnscdn" },
  { num:"15 — HLD", label:"Consistent Hashing",        sub:"Ring, vnodes, rate limiting algorithms",            type:"hld", id:"hashing" },
  { num:"16 — HLD", label:"Replication & SAGA",        sub:"Leader-follower, distributed transactions",         type:"hld", id:"replication" },
  { num:"17 — HLD", label:"Security",                  sub:"Auth, JWT, OAuth2, common attacks",                 type:"hld", id:"security" },
  { num:"18 — HLD", label:"Observability",             sub:"Logs, metrics, traces, SLO/SLA",                    type:"hld", id:"observability" },
  { num:"19 — LLD", label:"All 23 GoF Patterns",       sub:"Remaining Proxy, State, Memento, Visitor…",         type:"lld", id:"patterns2" },
  { num:"20 — LLD", label:"LLD Practice Problems",     sub:"Parking Lot, Elevator, BookMyShow",                 type:"lld", id:"lldproblems" },
  { num:"21 — HLD", label:"Real-World Systems",        sub:"YouTube, WhatsApp, Uber, Notification, Search",     type:"hld", id:"systems" },
  { num:"22 — HLD", label:"Capacity Estimation",       sub:"Back-of-envelope framework, worked example",        type:"hld", id:"capacity" },
];

/* ─── UPDATED NAV ITEMS ─── */
const NAV_ITEMS_FULL = [
  { id:"intro",        label:"Intro" },
  { id:"fundamentals", label:"LLD Basics" },
  { id:"lld",          label:"Class Design" },
  { id:"oop",          label:"Patterns" },
  { id:"concurrency",  label:"Concurrency" },
  { id:"apidesign",    label:"API Design" },
  { id:"patterns2",    label:"GoF Patterns" },
  { id:"lldproblems",  label:"LLD Problems" },
  { id:"hld",          label:"Architecture" },
  { id:"scalability",  label:"Scalability" },
  { id:"databases",    label:"Databases" },
  { id:"caching",      label:"Caching" },
  { id:"messaging",    label:"Messaging" },
  { id:"microservices",label:"Microservices" },
  { id:"dnscdn",       label:"DNS & CDN" },
  { id:"hashing",      label:"Hashing" },
  { id:"replication",  label:"Replication" },
  { id:"security",     label:"Security" },
  { id:"observability",label:"Observability" },
  { id:"cap",          label:"CAP" },
  { id:"realworld",    label:"Classic Examples" },
  { id:"systems",      label:"Real Systems" },
  { id:"capacity",     label:"Capacity Est." },
];

/* ─── RECAP ─── */
const RECAP_ITEMS = [
  { num:"LLD", label:"OOP + SOLID",            sub:"Pillars, principles, class design",            type:"lld" },
  { num:"LLD", label:"Core Design Patterns",   sub:"8 essential GoF patterns with code",           type:"lld" },
  { num:"LLD", label:"Concurrency",            sub:"Mutex, semaphore, deadlock, thread safety",    type:"lld" },
  { num:"LLD", label:"API Design",             sub:"REST, gRPC, GraphQL trade-offs",               type:"lld" },
  { num:"LLD", label:"All 23 GoF Patterns",    sub:"Proxy, State, Memento, Visitor + more",        type:"lld" },
  { num:"LLD", label:"LLD Practice",           sub:"Parking Lot, Elevator, BookMyShow",            type:"lld" },
  { num:"HLD", label:"Architecture",           sub:"Monolith → Microservices journey",             type:"hld" },
  { num:"HLD", label:"Scalability",            sub:"Load balancing, CDN, statelessness",           type:"hld" },
  { num:"HLD", label:"Databases",              sub:"SQL vs NoSQL, sharding, replication, SAGA",    type:"hld" },
  { num:"HLD", label:"Caching + Messaging",    sub:"Redis strategies, Kafka architecture",         type:"hld" },
  { num:"HLD", label:"Security",               sub:"JWT, OAuth2, common attack vectors",           type:"hld" },
  { num:"HLD", label:"Observability",          sub:"Logs, metrics, traces, SLO/SLA",              type:"hld" },
  { num:"HLD", label:"Consistent Hashing",     sub:"Ring, vnodes, rate limiting algorithms",       type:"hld" },
  { num:"HLD", label:"CAP + Consistency",      sub:"CP vs AP, consistency levels",                type:"hld" },
  { num:"HLD", label:"5 Real-World Systems",   sub:"YouTube, WhatsApp, Uber, Notification, Search",type:"hld" },
  { num:"HLD", label:"Capacity Estimation",    sub:"Back-of-envelope framework",                   type:"hld" },
];

const Recap = () => (
  <section className="recap-section" style={{ maxWidth:1000 }}>
    <div className="chapter-num" style={{ textAlign:"center" }}>Recap</div>
    <h2 className="chapter-title" style={{ fontSize:32, textAlign:"center", marginBottom:40 }}>Complete Coverage</h2>
    <div className="toc-grid">
      {RECAP_ITEMS.map(t => (
        <div key={t.label} className={`toc-item ${t.type}`}>
          <div className="toc-num">{t.num}</div>
          <div className="toc-label">{t.label}</div>
          <div className="toc-sub">{t.sub}</div>
        </div>
      ))}
    </div>
  </section>
);

/* ─── ROOT APP ─── */
export default function Sysvis() {
  const [progress, setProgress] = useState(0);
  const [activeNav, setActiveNav] = useState("intro");

  useEffect(() => {
    if (!document.getElementById("sysdesign-css")) {
      const tag = document.createElement("style");
      tag.id = "sysdesign-css";
      tag.textContent = GLOBAL_CSS;
      document.head.appendChild(tag);
    }
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
      let current = "intro";
      NAV_ITEMS_FULL.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el && window.scrollY >= el.offsetTop - 100) current = id;
      });
      setActiveNav(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <nav className="nav">
        <div className="nav-logo" onClick={() => window.scrollTo({ top:0, behavior:"smooth" })}>SysDesign.visual</div>
        <div className="nav-chapters">
          {NAV_ITEMS_FULL.map(({ id, label }) => (
            <button key={id} className={`nav-ch ${activeNav===id?"active":""}`} onClick={() => scrollTo(id)}>{label}</button>
          ))}
        </div>
      </nav>
      <div className="progress-track"><div className="progress-bar" style={{ width:`${progress}%` }} /></div>

      <Hero />

      {/* Updated TOC */}
      <div className="toc-section">
        <div className="toc-inner" style={{ maxWidth:1000 }}>
          <div className="toc-title">Contents — 22 Chapters · Full Coverage</div>
          <div className="toc-grid">
            {TOC_ITEMS_NEW.map(t => (
              <div key={t.id} className={`toc-item ${t.type}`} onClick={() => scrollTo(t.id)}>
                <div className="toc-num">{t.num}</div>
                <div className="toc-label">{t.label}</div>
                <div className="toc-sub">{t.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── LLD Part ── */}
      <SectionDivider part="Part One" title="Low Level Design" sub="The art of designing components, classes, and the interactions between them." />
      <Ch01 />
      <Ch02 />
      <Ch03 />
      <Ch12 />
      <Ch13 />
      <Ch19 />
      <Ch20 />

      {/* ── HLD Part ── */}
      <SectionDivider part="Part Two" title="High Level Design" sub="Architecting systems that are scalable, reliable, and maintainable at internet scale." />
      <Ch04 />
      <Ch05 />
      <Ch06 />
      <Ch07 />
      <Ch08 />
      <Ch09 />
      <Ch14 />
      <Ch15 />
      <Ch16 />
      <Ch17 />
      <Ch18 />
      <Ch10 />
      <Ch11 />
      <Ch21 />
      <Ch22 />

      <Recap />

      <footer>
        <h3>A Visual Guide to System Design</h3>
        <p>22 chapters · LLD + HLD · Complete interview preparation. Scroll-driven narrative inspired by r2d3.us.</p>
        <p>Content Scrollytelling by Yash</p>

      </footer>
    </>
  );
}
